import React from 'react';
export const renderJavaContent = (activeTopic) => {
  switch (activeTopic) {
    case 'What is Java?':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              What is Java?
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Java is a high-level, class-based, object-oriented programming language that is designed to have as few implementation dependencies as possible. It was developed by James Gosling at Sun Microsystems in 1995.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Features */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Key Pillars</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Platform Independent:</strong> Compiled Java code (Bytecode) runs on any machine with a JVM.</li>
                   <li>• <strong>Object-Oriented:</strong> Everything in Java is an Object, making it modular and reusable.</li>
                   <li>• <strong>Robust:</strong> Strong memory management and exception handling.</li>
                   <li>• <strong>Secure:</strong> Runs inside a virtual machine sandbox.</li>
                </ul>
             </div>

             <div className="p-6 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-2 italic text-sm">WORA Principle</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   "Write Once, Run Anywhere" means you develop Java code on Windows, and it can run on Linux or macOS without any changes, thanks to the <strong>Java Virtual Machine (JVM)</strong>.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">HelloWorld.java</span>
             </div>
             <pre className="text-red-200">
{`public class HelloWorld {
    public static void main(String[] args) {
        // The entry point of a Java program
        System.out.println("Hello, Koteshwar!"); 
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Enterprise Note:</strong> Java is the backbone of many banking systems and large-scale enterprise applications. For a software developer, understanding Java opens up massive opportunities in backend engineering.
             </p>
          </div>
        </div>
      );
      case 'Java Installation':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Installation & Environment Setup
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              To develop Java applications, you need to install the <strong>Java Development Kit (JDK)</strong>. The JDK includes the JRE (Runtime) and development tools like the compiler (<code>javac</code>).
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Step 1: JDK Installation */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Download JDK</h4>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 mb-4">
                   Download <strong>JDK 17 (LTS)</strong> or <strong>JDK 21</strong> from Oracle or Adoptium (OpenJDK). These are long-term support versions ideal for enterprise development.
                </p>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[9px] text-red-300">
                   Visit: oracle.com/java/technologies/downloads/
                </div>
             </div>

             {/* Step 2: Environment Variables */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">2. Set Environment Variables</h4>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 mb-2">
                   This allows you to run Java commands from any terminal window.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-500 italic">
                   <li>• Create <strong>JAVA_HOME</strong> variable pointing to JDK folder.</li>
                   <li>• Add <strong>%JAVA_HOME%\bin</strong> to the System Path.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">Verification Commands</span>
             </div>
             <pre className="text-red-200 text-xs">
{`# Open your terminal or CMD and type:

# Check if Java is installed
$ java -version

# Expected Output:
# java version "21.0.1" 2023-10-17 LTS
# Java(TM) SE Runtime Environment

# Check if the Compiler is ready
$ javac -version`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>IDE Recommendation:</strong> While you can write Java in any text editor, using <strong>IntelliJ IDEA</strong> or <strong>VS Code</strong> with the "Extension Pack for Java" is highly recommended for building professional projects like your <strong>HRMS</strong> or <strong>e-commerce</strong> backends.
             </p>
          </div>
        </div>
      );
      case 'Variables':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Variables & Declaration
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              In Java, a variable is a container that holds data while a Java program is executed. Java is a <strong>strongly typed</strong> language, meaning every variable must have a declared type.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Syntax Rules */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Declaration Syntax</h4>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 mb-4">
                   To create a variable, you must specify the type and assign it a value:
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg font-mono text-[10px] text-red-500">
                   type variableName = value;
                </div>
             </div>

             {/* Variable Categories */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Scope Types</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Local Variables:</strong> Declared inside a method.</li>
                   <li>• <strong>Instance Variables:</strong> Declared in a class but outside methods.</li>
                   <li>• <strong>Static Variables:</strong> Declared as static; shared across all instances.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">VariableExamples.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class VariableExamples {
    // Static Variable
    static String website = "senvra.ai";

    public static void main(String[] args) {
        // String Variable
        String name = "Koteshwar";
        
        // Integer Variable
        int age = 25;
        
        // Constant (Value cannot be changed)
        final double PI = 3.14159;
        
        System.out.println(name + " manages " + website);
        System.out.println("Age: " + age);
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Pro Tip:</strong> Use the <code>final</code> keyword when you want a variable to be read-only (like a constant). This is great for configuration values in your <strong>senvra ai</strong> project that shouldn't change during runtime.
             </p>
          </div>
        </div>
      );
      case 'Input/Output':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Input & Output (I/O)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              In Java, <strong>Output</strong> is handled via <code>System.out</code>, while <strong>Input</strong> is most commonly managed using the <code>Scanner</code> class from the <code>java.util</code> package.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Standard Output */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Standard Output</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>println()</code>: Prints text and moves the cursor to a new line.</li>
                   <li>• <code>print()</code>: Prints text without adding a new line.</li>
                   <li>• <code>printf()</code>: Used for formatted output (e.g., controlling decimal places).</li>
                </ul>
             </div>

             {/* Standard Input */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Scanner Class (Input)</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   The Scanner class reads primitive types and strings using methods like <code>nextLine()</code>, <code>nextInt()</code>, and <code>nextDouble()</code>.
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded font-mono text-[9px] text-red-500">
                   import java.util.Scanner;
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">UserInput.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.Scanner; // Import the utility

public class UserInput {
    public static void main(String[] args) {
        // Create Scanner object
        Scanner scanner = new Scanner(System.in);

        System.out.print("Enter your username: ");
        String username = scanner.nextLine(); // Read String

        System.out.print("Enter your experience (years): ");
        int years = scanner.nextInt(); // Read Integer

        // Formatted Output
        System.out.println("Dev: " + username + " | Exp: " + years + " years");

        scanner.close(); // Closing to free resources
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Best Practice:</strong> Always use <code>scanner.close()</code> when you are finished. In professional applications like your <strong>HRMS</strong> or <strong>Travel platform</strong>, failing to close resources can lead to memory leaks.
             </p>
          </div>
        </div>
      );
      case 'Operators':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Operators
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Operators are symbols that perform operations on variables and values. Java provides several types of operators to handle mathematical, logical, and bitwise manipulations.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Arithmetic & Assignment */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Arithmetic & Assignment</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Arithmetic:</strong> <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>, <code>%</code> (Modulus - returns remainder).</li>
                   <li>• <strong>Assignment:</strong> <code>=</code>, <code>+=</code>, <code>-=</code>, <code>*=</code> (Shorthand updates).</li>
                   <li>• <strong>Unary:</strong> <code>++</code> (Increment), <code>--</code> (Decrement).</li>
                </ul>
             </div>

             {/* Comparison & Logical */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Comparison & Logical</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Relational:</strong> <code>==</code>, <code>!=</code>, <code>&gt;</code>, <code>&lt;</code>, <code>&gt;=</code>.</li>
                   <li>• <strong>Logical:</strong> <code>&&</code> (Logical AND), <code>||</code> (Logical OR), <code>!</code> (Logical NOT).</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">OperatorsInAction.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class OperatorsInAction {
    public static void main(String[] args) {
        int x = 10;
        int y = 3;

        // Modulus operator
        int remainder = x % y; // Result: 1

        // Logical AND (Both must be true)
        boolean canAccess = (x > 5) && (y < 5); // Result: true

        // Ternary Operator (Condition ? TrueValue : FalseValue)
        String parity = (x % 2 == 0) ? "Even" : "Odd";

        System.out.println("Remainder: " + remainder);
        System.out.println("Parity: " + parity);
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Tip:</strong> Use the <strong>Ternary Operator</strong> to simplify simple <code>if-else</code> logic into a single line. This is particularly useful in React and React Native when you need to perform conditional rendering.
             </p>
          </div>
        </div>
      );
      case 'Type Casting':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Type Casting
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Type casting is the process of converting a value from one data type to another. In Java, this is categorized into two main types based on whether the conversion is automatic or manual.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Widening Casting */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-green-600 mb-3 text-xs uppercase italic">1. Widening (Implicit)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Converting a smaller type to a larger type size. This happens <strong>automatically</strong> because there is no risk of data loss.
                </p>
                <div className="text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 p-2 rounded-lg">
                   byte → short → char → int → long → float → double
                </div>
             </div>

             {/* Narrowing Casting */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">2. Narrowing (Explicit)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Converting a larger type to a smaller type size. This must be done <strong>manually</strong> using parentheses.
                </p>
                <div className="text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 p-2 rounded-lg">
                   double → float → long → int → char → short → byte
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">CastingDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class CastingDemo {
    public static void main(String[] args) {
        // Widening: int to double
        int myInt = 9;
        double myDouble = myInt; // Automatic
        
        System.out.println("Int: " + myInt);       // 9
        System.out.println("Double: " + myDouble); // 9.0

        // Narrowing: double to int
        double pi = 3.14;
        int intPi = (int) pi; // Manual cast required
        
        System.out.println("Double PI: " + pi);    // 3.14
        System.out.println("Integer PI: " + intPi); // 3 (Data loss)
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Warning:</strong> Narrowing casting can lead to <strong>data loss</strong> (like losing decimal precision). Always double-check your logic when forcing a large value into a smaller container.
             </p>
          </div>
        </div>
      );
      case 'Comments':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Comments & Documentation
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Comments are non-executable statements used to explain the logic of the code, making it easier for humans to read and maintain. They are also vital for generating professional API documentation.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {/* Single-line */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Single-line</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Used for brief notes. Everything after <code>//</code> on that line is ignored by the compiler.
                </p>
             </div>

             {/* Multi-line */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Multi-line</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Starts with <code>/*</code> and ends with <code>*/</code>. Useful for long explanations or disabling blocks of code.
                </p>
             </div>

             {/* Javadoc */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-700 mb-3 text-xs uppercase italic">Documentation</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Starts with <code>/**</code>. Used by the Javadoc tool to create HTML-based API documentation.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">DocumentationDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`/**
 * This class demonstrates the three types of Java comments.
 * @author Koteshwar
 */
public class DocumentationDemo {

    public static void main(String[] args) {
        // This is a single-line comment: initializing x
        int x = 10; 

        /* This is a multi-line comment.
           It explains that we are performing
           an addition operation below. */
        int y = 20;

        System.out.println("Result: " + (x + y));
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Professional Advice:</strong> Good code is often "self-documenting" through clear variable names. Only use comments to explain the <strong>"Why"</strong> behind complex logic, not the <strong>"What"</strong> of simple statements.
             </p>
          </div>
        </div>
      );
      case 'Indentation':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Indentation & Code Style
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              While Java uses curly braces <code>{`{ }`}</code> to define code blocks, indentation is used to visually organize the hierarchy. Proper indentation makes it clear which statements belong to which class, method, or loop.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The Standard */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">The 4-Space Rule</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   The industry standard is to use <strong>4 spaces</strong> (or 1 Tab) for every new level of nesting.
                </p>
                <ul className="text-[10px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Level 1:</strong> Inside the Class.</li>
                   <li>• <strong>Level 2:</strong> Inside the Method.</li>
                   <li>• <strong>Level 3:</strong> Inside Loops/Conditionals.</li>
                </ul>
             </div>

             {/* Formatting Shortcut */}
             <div className="p-6 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-2 italic text-sm">Auto-Formatting</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   Don't format manually! In VS Code or IntelliJ, you can auto-fix your indentation instantly.
                </p>
                <div className="mt-2 text-[9px] font-mono bg-white/50 dark:bg-black/20 p-2 rounded">
                   Shift + Alt + F (VS Code)
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">CleanStructure.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class CleanStructure { // Level 0
    public static void main(String[] args) { // Level 1
        
        int count = 5;
        
        if (count > 0) { // Level 2
            for (int i = 0; i < count; i++) { // Level 3
                System.out.println("Indent Level 4");
            }
        }
        
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Developer Insight:</strong> If you are working on a collaborative project like your <strong>senvra ai</strong> backend, consistent indentation is non-negotiable. It ensures that any developer can jump in and understand the logic flow at a glance.
             </p>
          </div>
        </div>
      );
      case 'Keywords':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Reserved Keywords
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Java has a set of <strong>50+ reserved keywords</strong>. These words serve as the building blocks of the language's syntax. Since they have special meaning to the JVM, they are restricted from being used as identifiers.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Common Categories */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Key Categories</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Data Types:</strong> <code>int</code>, <code>double</code>, <code>boolean</code>, <code>char</code>.</li>
                   <li>• <strong>Access Modifiers:</strong> <code>public</code>, <code>private</code>, <code>protected</code>.</li>
                   <li>• <strong>Control Flow:</strong> <code>if</code>, <code>else</code>, <code>switch</code>, <code>for</code>, <code>while</code>.</li>
                   <li>• <strong>Class Related:</strong> <code>class</code>, <code>interface</code>, <code>extends</code>, <code>implements</code>.</li>
                </ul>
             </div>

             {/* Forbidden Rule */}
             <div className="p-6 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-2 italic text-sm">The Golden Rule</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   You cannot name a variable <code>int class = 5;</code> because <code>class</code> is a reserved keyword. Doing so will result in a <strong>Compile-Time Error</strong>.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">KeywordUsage.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// 'public' and 'class' are keywords
public class KeywordUsage { 

    // 'static', 'void', and 'main' are keywords
    public static void main(String[] args) { 
        
        // 'int' and 'new' are keywords
        int[] numbers = new int[5]; 
        
        // 'if' and 'return' are keywords
        if (numbers.length > 0) {
            System.out.println("Processing...");
        }
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> While <code>true</code>, <code>false</code>, and <code>null</code> might look like keywords, they are actually <strong>literals</strong>. However, they are still reserved and cannot be used as identifiers in your code.
             </p>
          </div>
        </div>
      );
      case 'Basic Programs':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Fundamental Java Practice Programs
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Mastering these basic programs is the first step toward building complex logic for platforms like your <strong>organic e-commerce store</strong> or <strong>HRMS</strong>. These examples combine variables, operators, and I/O.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Program 1: Sum & Average */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Math Operations</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   A program that takes three numbers and calculates their total and average value.
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg font-mono text-[9px] text-slate-600 dark:text-slate-300">
                   Concepts: <code>Scanner</code>, <code>Arithmetic Operators</code>
                </div>
             </div>

             {/* Program 2: Swap Variables */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Swapping Logic</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Swapping two values using a third temporary variable—a foundational logic for sorting algorithms.
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg font-mono text-[9px] text-slate-600 dark:text-slate-300">
                   Concepts: <code>Variables</code>, <code>Temporary Storage</code>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">MathAndSwap.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.Scanner;

public class BasicPractice {
    public static void main(String[] args) {
        Scanner sc = new Scanner(System.in);

        // --- Calculation Program ---
        System.out.print("Enter first number: ");
        int a = sc.nextInt();
        System.out.print("Enter second number: ");
        int b = sc.nextInt();

        int sum = a + b;
        double avg = sum / 2.0;
        System.out.println("Sum: " + sum + " | Average: " + avg);

        // --- Swapping Program ---
        System.out.println("\\nSwapping values...");
        int temp = a;
        a = b;
        b = temp;
        System.out.println("New a: " + a + " | New b: " + b);
        
        sc.close();
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Challenge:</strong> Can you swap the numbers <code>a</code> and <code>b</code> <strong>without</strong> using the <code>temp</code> variable? (Hint: Use addition and subtraction operators).
             </p>
          </div>
        </div>
      );
      case 'JDK, JRE, and JVM':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Architecture: JDK, JRE, and JVM
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              These three components are nested within each other. The <strong>JVM</strong> is the heart that runs the code, the <strong>JRE</strong> provides the environment, and the <strong>JDK</strong> provides the tools to build it.
            </p>
          </section>

          [Image of a nested diagram showing JDK containing JRE and Development Tools, and JRE containing JVM and Library Classes]

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {/* JVM */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. JVM</h4>
                <p className="text-[10px] text-slate-500 mb-2 font-bold">(Java Virtual Machine)</p>
                <p className="text-[11px] text-slate-600 dark:text-slate-400">
                   The interpreter that executes <strong>Bytecode</strong>. It is platform-dependent, which makes Java code platform-independent.
                </p>
             </div>

             {/* JRE */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. JRE</h4>
                <p className="text-[10px] text-slate-500 mb-2 font-bold">(Java Runtime Environment)</p>
                <p className="text-[11px] text-slate-600 dark:text-slate-400">
                   A package that contains the JVM and core libraries (like <code>rt.jar</code>) needed to <strong>run</strong> Java programs.
                </p>
             </div>

             {/* JDK */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-700 mb-3 text-xs uppercase italic">3. JDK</h4>
                <p className="text-[10px] text-slate-500 mb-2 font-bold">(Java Development Kit)</p>
                <p className="text-[11px] text-slate-600 dark:text-slate-400">
                   The full software kit for <strong>developing</strong> Java apps. It includes the JRE plus tools like <code>javac</code> (compiler).
                </p>
             </div>
          </div>

          [Image of the Java compilation and execution flow: Source Code (.java) to Compiler (javac) to Bytecode (.class) to JVM execution on various OS]

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">The "Math" of Java Architecture</span>
             </div>
             <div className="text-red-200 text-xs space-y-4">
                <p className="border-l-2 border-red-500 pl-4 py-1">
                   <strong>JRE</strong> = JVM + Libraries (to run code)
                </p>
                <p className="border-l-2 border-red-500 pl-4 py-1">
                   <strong>JDK</strong> = JRE + Development Tools (to write/compile code)
                </p>
             </div>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Interview Insight:</strong> If a client only wants to <i>run</i> your <strong>JJROrganic</strong> desktop app, they only need the <strong>JRE</strong>. But for you to <i>build</i> the <strong>senvra ai</strong> backend, you must have the <strong>JDK</strong> installed.
             </p>
          </div>
        </div>
      );
      case 'Syntax':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Basic Syntax
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Every Java program follows a strict structure. From case sensitivity to the way blocks are defined, understanding these rules is essential for writing error-free code.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Fundamental Rules */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">General Rules</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Case Sensitivity:</strong> <code>MyVariable</code> and <code>myvariable</code> are different.</li>
                   <li>• <strong>File Name:</strong> Must exactly match the <strong>Public Class</strong> name (e.g., <code>Main.java</code>).</li>
                   <li>• <strong>Statements:</strong> Every statement must end with a semicolon (<code>;</code>).</li>
                </ul>
             </div>

             {/* The Main Method */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">The Entry Point</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Every Java application must contain a <code>main()</code> method. This is where the JVM starts executing your code.
                </p>
                <code className="text-[9px] text-orange-500 font-mono">public static void main(String[] args)</code>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">HelloWorld.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// 1. Class definition
public class HelloWorld { 

    // 2. Main method - The starting point
    public static void main(String[] args) { 
        
        // 3. Statement - Printing text
        System.out.println("Hello, Koteshwar!"); 
        
    } // Closing Method
} // Closing Class`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Key Concept:</strong> Java uses curly braces <code>{`{ }`}</code> to group code into blocks. Whether it's a class or a method, everything inside the braces belongs to that block. This is different from Python, which uses indentation to define blocks.
             </p>
          </div>
        </div>
      );
      case 'Data Types':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600">
              Java Data Types
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Data types specify the different sizes and values that can be stored in the variable. Java is a <strong>statically-typed</strong> language, meaning all variables must be declared before they can be used.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Primitive Data Types */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Primitive Data Types</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   These are predefined by the language and named by a keyword. They do not share state with other primitive values.
                </p>
                <div className="space-y-2">
                   <div className="flex justify-between text-[10px] border-b border-slate-100 dark:border-slate-800 pb-1">
                      <span className="font-mono text-red-500">int, long</span>
                      <span className="text-slate-400 italic">Whole Numbers</span>
                   </div>
                   <div className="flex justify-between text-[10px] border-b border-slate-100 dark:border-slate-800 pb-1">
                      <span className="font-mono text-red-500">float, double</span>
                      <span className="text-slate-400 italic">Decimals</span>
                   </div>
                   <div className="flex justify-between text-[10px] border-b border-slate-100 dark:border-slate-800 pb-1">
                      <span className="font-mono text-red-500">char</span>
                      <span className="text-slate-400 italic">Single Character</span>
                   </div>
                   <div className="flex justify-between text-[10px]">
                      <span className="font-mono text-red-500">boolean</span>
                      <span className="text-slate-400 italic">True / False</span>
                   </div>
                </div>
             </div>

             {/* Non-Primitive Data Types */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Non-Primitive (Reference)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   These are created by the programmer and are not predefined in Java (except for Strings). They can be used to call methods to perform certain actions.
                </p>
                <ul className="text-[10px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Strings:</strong> Sequence of characters.</li>
                   <li>• <strong>Arrays:</strong> Collection of similar types.</li>
                   <li>• <strong>Classes & Interfaces:</strong> User-defined structures.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">DataTypeExamples.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class DataTypeExamples {
    public static void main(String[] args) {
        // Primitive: Stores the actual value
        int myID = 101; 
        double price = 19.99;
        boolean isComplete = false;

        // Non-Primitive: Stores a reference to the object
        String message = "Welcome to senvra.ai";
        int[] scores = {90, 85, 88};

        System.out.println("ID: " + myID);
        System.out.println("Message: " + message);
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Memory Note:</strong> Primitive types are stored in the <strong>Stack</strong> (fast access), while Non-primitive types are stored in the <strong>Heap</strong> (flexible size). Understanding this is vital for optimizing performance in your data science projects.
             </p>
          </div>
        </div>
      );
      case 'Searching':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Searching Algorithms
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Searching is the process of finding the location of an element within a data structure. In Java, we primarily focus on <strong>Linear Search</strong> for unsorted data and <strong>Binary Search</strong> for sorted data.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Linear Search */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Linear Search</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Checks every element one by one from the start until the target is found.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Requirement:</strong> None (works on any array).</li>
                   <li>• <strong>Time Complexity:</strong> $O(n)$</li>
                </ul>
             </div>

             {/* Binary Search */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Binary Search</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Repeatedly divides the search interval in half. Much faster for large datasets.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Requirement:</strong> Array <strong>must</strong> be sorted.</li>
                   <li>• <strong>Time Complexity:</strong> $O(\log n)$</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">SearchAlgorithms.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class SearchAlgorithms {
    // Linear Search Implementation
    public static int linearSearch(int[] arr, int target) {
        for (int i = 0; i < arr.length; i++) {
            if (arr[i] == target) return i;
        }
        return -1;
    }

    // Binary Search Implementation
    public static int binarySearch(int[] arr, int target) {
        int left = 0, right = arr.length - 1;
        while (left <= right) {
            int mid = left + (right - left) / 2;
            if (arr[mid] == target) return mid;
            if (arr[mid] < target) left = mid + 1;
            else right = mid - 1;
        }
        return -1;
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Performance Tip:</strong> If you are searching for a specific product ID in your <strong>JJROrganic</strong> store among thousands of items, always sort the data first and use Binary Search. The difference between 1,000 steps ($O(n)$) and 10 steps ($O(\log n)$) is massive for user experience.
             </p>
          </div>
        </div>
      );case 'Sorting':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Sorting Algorithms in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Sorting is the arrangement of data in a specific order (ascending or descending). It is a fundamental concept in <strong>Data Structures & Algorithms (DSA)</strong> used to optimize searching and data organization.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Built-in Sorting */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Built-in (Arrays.sort)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Java uses an optimized <strong>Dual-Pivot Quicksort</strong> for primitives. It is the most efficient choice for production apps.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600">
                   <li>• <strong>Complexity:</strong> $O(n \log n)$</li>
                   <li>• <strong>Usage:</strong> Best for large datasets.</li>
                </ul>
             </div>

             {/* Manual Sorting */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Manual (Bubble Sort)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   A simple comparison-based algorithm where adjacent elements are swapped if they are in the wrong order.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600">
                   <li>• <strong>Complexity:</strong> $O(n^2)$</li>
                   <li>• <strong>Usage:</strong> Primarily for logic building.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">SortingLogic.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class SortingLogic {
    public static void main(String[] args) {
        int[] arr = {64, 34, 25, 12, 22};
        int n = arr.length;

        // Bubble Sort Implementation
        for (int i = 0; i < n - 1; i++) {
            for (int j = 0; j < n - i - 1; j++) {
                if (arr[j] > arr[j + 1]) {
                    // Swap elements
                    int temp = arr[j];
                    arr[j] = arr[j + 1];
                    arr[j + 1] = temp;
                }
            }
        }

        System.out.println("Sorted Array: ");
        for (int num : arr) System.out.print(num + " ");
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Interview Insight:</strong> While Bubble Sort is easy to understand, never use it in a real-world project like your <strong>JJROrganic</strong> store. The $O(n^2)$ complexity means that if you have 10,000 products, it could take millions of operations to sort them, whereas <code>Arrays.sort()</code> would do it in a fraction of the time.
             </p>
          </div>
        </div>
      );
      case 'Array Methods':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Arrays Utility Class
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Manually writing logic to sort or search through arrays is time-consuming. Java provides the <code>java.util.Arrays</code> class, which contains highly optimized static methods for common operations.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Common Static Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Essential Utility Methods</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Arrays.sort():</strong> Sorts the array in ascending order ($O(n \log n)$ efficiency).</li>
                   <li>• <strong>Arrays.binarySearch():</strong> Finds the index of a value (requires a sorted array).</li>
                   <li>• <strong>Arrays.toString():</strong> Converts the array into a readable String format <code>[1, 2, 3]</code>.</li>
                   <li>• <strong>Arrays.equals():</strong> Compares two arrays for equality of content and order.</li>
                </ul>
             </div>

             {/* Import Requirement */}
             <div className="p-6 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-2 italic text-sm">Prerequisite</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed mb-2">
                   To use these methods, you must import the utility at the very top of your file:
                </p>
                <code className="text-[10px] text-red-500 font-mono">import java.util.Arrays;</code>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">ArraysToolkit.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.Arrays;

public class ArraysToolkit {
    public static void main(String[] args) {
        int[] data = {50, 10, 30, 20, 40};

        // 1. Convert array to String for easy printing
        System.out.println("Original: " + Arrays.toString(data));

        // 2. Sorting the array
        Arrays.sort(data);
        System.out.println("Sorted: " + Arrays.toString(data));

        // 3. Binary Search for element 30
        int index = Arrays.binarySearch(data, 30);
        System.out.println("Index of 30: " + index);

        // 4. Fill an array with a specific value
        int[] empty = new int[3];
        Arrays.fill(empty, 7); // [7, 7, 7]
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Performance Fact:</strong> <code>Arrays.sort()</code> uses a <strong>Dual-Pivot Quicksort</strong> algorithm. For your <strong>Data Science</strong> interests, knowing this is key because it is significantly faster than the basic Bubble Sort ($O(n^2)$) we discussed earlier.
             </p>
          </div>
        </div>
      );
      case '2D Arrays':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Two-Dimensional (2D) Arrays
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A 2D array is essentially an <strong>array of arrays</strong>. It allows you to store data in a grid-like format consisting of rows and columns, similar to an Excel spreadsheet or a database table.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Concept of Rows and Columns */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">The Matrix Structure</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>First Index:</strong> Represents the <strong>Row</strong> number.</li>
                   <li>• <strong>Second Index:</strong> Represents the <strong>Column</strong> number.</li>
                   <li>• <strong>Syntax:</strong> <code>dataType[row][column]</code>.</li>
                </ul>
             </div>

             {/* Initialization Styles */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Declaration</h4>
                <p className="text-[11px] text-slate-500 mb-2">Defining a 3x3 grid:</p>
                <code className="text-[9px] block bg-slate-100 dark:bg-slate-800 p-2 rounded text-red-500 mb-2">
                   int[][] matrix = new int[3][3];
                </code>
                <code className="text-[9px] block bg-slate-100 dark:bg-slate-800 p-2 rounded text-red-500">
                   int[][] matrix = {"{ {1,2}, {3,4} }"};
                </code>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">MatrixTraversal.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class MatrixTraversal {
    public static void main(String[] args) {
        // Initializing a 2x3 matrix (2 rows, 3 columns)
        int[][] grid = {
            {10, 20, 30},
            {40, 50, 60}
        };

        // Accessing a specific element (Row 1, Column 2)
        System.out.println("Element at [1][2]: " + grid[1][2]); // Output: 60

        // Iterating through the entire grid
        for (int i = 0; i < grid.length; i++) { // Outer loop for rows
            for (int j = 0; j < grid[i].length; j++) { // Inner loop for columns
                System.out.print(grid[i][j] + " ");
            }
            System.out.println(); // Move to next row
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Logic Note:</strong> In Java, the sub-arrays do not have to be the same length. This creates what is known as a <strong>Jagged Array</strong>. For example, <code>grid[0]</code> could have 3 elements while <code>grid[1]</code> has 5.
             </p>
          </div>
        </div>
      );
      case '1D Arrays':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              One-Dimensional (1D) Arrays
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              An array is a collection of similar types of elements. It is a data structure where we store multiple variables of the same type in <strong>contiguous memory locations</strong>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Properties */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Array Characteristics</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Fixed Size:</strong> The length of an array is established when it is created.</li>
                   <li>• <strong>Index-Based:</strong> Elements are accessed by index, starting at <code>0</code>.</li>
                   <li>• <strong>Reference Type:</strong> Arrays are treated as objects in Java.</li>
                </ul>
             </div>

             {/* Syntax & Declaration */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Declaration Styles</h4>
                <p className="text-[11px] text-slate-500 mb-2">You can declare an array with a specific size or with immediate values:</p>
                <code className="text-[9px] block bg-slate-100 dark:bg-slate-800 p-2 rounded text-red-500 mb-2">
                   int[] arr = new int[5]; // Size only
                </code>
                <code className="text-[9px] block bg-slate-100 dark:bg-slate-800 p-2 rounded text-red-500">
                   int[] arr = {"{1, 2, 3}"}; // Literal values
                </code>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">ArrayOperations.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class ArrayOperations {
    public static void main(String[] args) {
        // Declaration and Initialization
        int[] scores = {95, 88, 72, 91};

        // Accessing elements
        System.out.println("First Score: " + scores[0]); 

        // Modifying elements
        scores[2] = 80;

        // Getting length
        System.out.println("Array size: " + scores.length);

        // Traversing using a loop
        for (int i = 0; i < scores.length; i++) {
            System.out.print(scores[i] + " ");
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>DSA Link:</strong> Because arrays provide <strong>constant time access ($O(1)$)</strong> to any element via its index, they are incredibly fast for read operations. However, inserting or deleting elements from the middle is slow ($O(n)$) because other elements must be shifted.
             </p>
          </div>
        </div>
      );
      case 'break / continue':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Break & Continue Statements
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Loop control statements allow you to alter the normal flow of execution. While <strong>break</strong> stops the loop entirely, <strong>continue</strong> only skips the current iteration.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Break Statement */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Break</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Immediately terminates the loop or switch statement. The program control resumes at the next statement following the loop.
                </p>
                <code className="text-[9px] text-red-400 font-mono">if (condition) break;</code>
             </div>

             {/* Continue Statement */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Continue</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Skips the remaining code in the current iteration and jumps directly to the next iteration of the loop.
                </p>
                <code className="text-[9px] text-orange-400 font-mono">if (condition) continue;</code>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">ControlFlowDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class ControlFlowDemo {
    public static void main(String[] args) {
        
        System.out.println("Using Break:");
        for (int i = 1; i <= 5; i++) {
            if (i == 3) break; // Exit loop when i is 3
            System.out.print(i + " ");
        } // Output: 1 2

        System.out.println("\\nUsing Continue:");
        for (int j = 1; j <= 5; j++) {
            if (j == 3) continue; // Skip 3, go to 4
            System.out.print(j + " ");
        } // Output: 1 2 4 5
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>DSA Use Case:</strong> Use <code>break</code> when searching for an item in a list (like an employee ID in your <strong>HRMS</strong> database). Once found, there's no need to check the rest of the list, saving processing time ($O(n)$ optimization).
             </p>
          </div>
        </div>
      );
      case 'loops (for, while, do-while)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Loops (Iteration)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Loops are used to execute a block of code repeatedly as long as a specified condition is true. Java provides three main types of loops to handle different iteration scenarios.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {/* For Loop */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">For Loop</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Best used when you know exactly how many times the code should run.
                </p>
                <code className="text-[9px] text-red-400 font-mono">for(init; cond; update)</code>
             </div>

             {/* While Loop */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">While Loop</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Checks the condition <strong>before</strong> executing the block. Ideal for variable iterations.
                </p>
                <code className="text-[9px] text-orange-400 font-mono">while(condition)</code>
             </div>

             {/* Do-While Loop */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-700 mb-3 text-xs uppercase italic">Do-While Loop</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Checks the condition <strong>after</strong> execution. Guarantees at least one run.
                </p>
                <code className="text-[9px] text-red-700 font-mono">do {"{...}"} while(cond);</code>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">LoopsExample.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class LoopsExample {
    public static void main(String[] args) {
        // 1. For Loop: 0 to 4
        for (int i = 0; i < 5; i++) {
            System.out.println("Iteration: " + i);
        }

        // 2. While Loop: Variable condition
        int count = 0;
        while (count < 3) {
            System.out.println("Count: " + count);
            count++;
        }

        // 3. Do-While: Executed at least once
        int x = 10;
        do {
            System.out.println("This prints once even if x < 5");
            x++;
        } while (x < 5);
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Infinite Loops:</strong> Be careful! If your loop condition never becomes false (e.g., you forget to increment your counter), the program will run forever and crash your application. In a <strong>Full-Stack</strong> environment, this can lead to high server CPU usage.
             </p>
          </div>
        </div>
      );
      case 'switch':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Switch Statement
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The <code>switch</code> statement is a multi-way branch statement. It provides an easy way to dispatch execution to different parts of code based on the value of an expression. It is a cleaner alternative to a long <code>if-else-if</code> ladder.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Keywords */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Switch Components</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>case:</strong> A constant value to be compared with the expression.</li>
                   <li>• <strong>break:</strong> Terminates the switch block; prevents "fall-through."</li>
                   <li>• <strong>default:</strong> Executes if no case matches (optional but recommended).</li>
                </ul>
             </div>

             {/* Supported Types */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Supported Data Types</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   The switch expression can be of the following types:
                </p>
                <div className="flex flex-wrap gap-2">
                   {['byte', 'short', 'int', 'char', 'String', 'Enums'].map(type => (
                      <span key={type} className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-[9px] font-mono">{type}</span>
                   ))}
                </div>
             </div>
          </div>

          [Image of a flowchart for Java switch case statement showing the expression evaluation and branching to different cases with a default path]

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">SwitchDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class SwitchDemo {
    public static void main(String[] args) {
        int day = 3;
        String dayType;

        switch (day) {
            case 1: case 2: case 3: case 4: case 5:
                dayType = "Weekday";
                break;
            case 6: case 7:
                dayType = "Weekend";
                break;
            default:
                dayType = "Invalid day";
                break;
        }
        System.out.println("Type: " + dayType);
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>The "Fall-Through" Effect:</strong> If you omit the <code>break</code> statement, execution will continue into the next case regardless of whether it matches the expression. This is a common logic bug but can be used intentionally for grouping cases.
             </p>
          </div>
        </div>
      );
      case 'if / else':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Conditional Statements (if-else)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Conditional statements allow your program to make decisions and execute different blocks of code based on whether a condition is <strong>true</strong> or <strong>false</strong>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Simple If-Else */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Simple If-Else</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Used for a binary choice. If the condition is true, the <code>if</code> block runs; otherwise, the <code>else</code> block runs.
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded font-mono text-[9px] text-red-500">
                   if (condition) {"{ ... }"} else {"{ ... }"}
                </div>
             </div>

             {/* Else-If Ladder */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Else-If Ladder</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Used when you have multiple conditions to check in a sequence. Only the first true block will execute.
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded font-mono text-[9px] text-orange-500">
                   else if (condition2) {"{ ... }"}
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">DecisionMaking.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class DecisionMaking {
    public static void main(String[] args) {
        int age = 18;

        // Basic If-Else
        if (age >= 18) {
            System.out.println("You are an adult.");
        } else {
            System.out.println("You are a minor.");
        }

        // Else-If Ladder for Grading
        int marks = 85;
        if (marks >= 90) {
            System.out.println("Grade: A+");
        } else if (marks >= 80) {
            System.out.println("Grade: A");
        } else {
            System.out.println("Grade: B");
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Tip:</strong> For your <strong>HRMS system</strong>, you might use <code>if-else</code> to check user roles (Admin vs Employee) or to validate leave balance before approving a request. Keep your conditions simple and clean for better maintainability.
             </p>
          </div>
        </div>
      );
      case 'nested loops':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Nested Loops
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              When we place a loop inside the body of another loop, it is called nesting. This is most commonly used for traversing grids (2D arrays) and building complex logical structures.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The Clock Analogy */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">How it works</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Think of the <strong>Outer Loop</strong> as the "Hour Hand" and the <strong>Inner Loop</strong> as the "Minute Hand". The minute hand must finish a full circle before the hour hand moves once.
                </p>
             </div>

             {/* Performance Impact */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Time Complexity</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   In your <strong>DSA</strong> studies, note that nesting loops usually leads to <strong>$O(n^2)$</strong> complexity. If the outer runs $N$ times and inner runs $M$ times, the total runs are $N \times M$.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">PatternPrint.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class PatternPrint {
    public static void main(String[] args) {
        // Outer loop for Rows
        for (int i = 1; i <= 3; i++) {
            
            // Inner loop for Columns
            for (int j = 1; j <= 3; j++) {
                System.out.print(i + "," + j + " ");
            }
            
            System.out.println(); // Moves to next line after inner loop finishes
        }
    }
}
/* Output:
   1,1 1,2 1,3 
   2,1 2,2 2,3 
   3,1 3,2 3,3 
*/`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Hint:</strong> When building features for your <strong>HRMS</strong> or <strong>Travel platform</strong>, avoid nesting loops more than twice if possible. Deep nesting makes the code harder to read and can cause performance bottlenecks on the server side.
             </p>
          </div>
        </div>
      );
      case 'String class':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              The String Class
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A <code>String</code> is a non-primitive data type that behaves like an object. Once created, a String's value cannot be changed. This unique behavior makes it both secure and efficient.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Core Concepts */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Key Properties</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Immutability:</strong> Strings are constant; their values cannot be changed after creation.</li>
                   <li>• <strong>Objects:</strong> Even a simple literal like <code>"Hello"</code> is an instance of the String class.</li>
                   <li>• <strong>The Pool:</strong> Java uses a special memory area called the <strong>String Constant Pool</strong>.</li>
                </ul>
             </div>

             {/* Creation Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">How to Create</h4>
                <p className="text-[11px] text-slate-500 mb-2">There are two ways to initialize a String:</p>
                <code className="text-[9px] block bg-slate-100 dark:bg-slate-800 p-2 rounded text-red-500 mb-2">
                   String s1 = "Literal"; // Checks pool first
                </code>
                <code className="text-[9px] block bg-slate-100 dark:bg-slate-800 p-2 rounded text-red-500">
                   String s2 = new String("Object"); // Always creates new object in Heap
                </code>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">StringBasics.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class StringBasics {
    public static void main(String[] args) {
        // String Literal
        String name1 = "Koteshwar"; 
        
        // Another literal with same value
        String name2 = "Koteshwar"; 
        
        // This returns 'true' because both point to the same memory in the Pool
        System.out.println(name1 == name2); 

        // Creating with 'new' keyword
        String name3 = new String("Koteshwar"); 
        
        // This returns 'false' because 'new' forces a fresh object in the Heap
        System.out.println(name1 == name3); 
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why Immutability?</strong> Since Strings are used for sensitive data (like passwords, database URLs, and file paths in your <strong>HRMS</strong> or <strong>Travel platform</strong>), making them immutable prevents unauthorized changes after the data has been validated.
             </p>
          </div>
        </div>
      );
      case 'StringBuilder':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java StringBuilder
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              <code>StringBuilder</code> is used when you need to modify strings frequently. Unlike <code>String</code>, it doesn't create a new object on every change, and unlike <code>StringBuffer</code>, it doesn't have the overhead of synchronization, making it the fastest option for text processing.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Advantages */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Why use StringBuilder?</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>High Performance:</strong> The fastest way to concatenate strings in a loop.</li>
                   <li>• <strong>Memory Efficient:</strong> Works with an internal resizable array.</li>
                   <li>• <strong>Non-Thread-Safe:</strong> Designed for single-threaded use cases.</li>
                </ul>
             </div>

             {/* Core Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Manipulation Methods</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>append()</code>: Most common way to add text to the end.</li>
                   <li>• <code>replace(start, end, str)</code>: Replaces a specific portion.</li>
                   <li>• <code>capacity()</code>: Returns the current storage space.</li>
                   <li>• <code>setCharAt(index, char)</code>: Modifies a single character.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">BuilderDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class BuilderDemo {
    public static void main(String[] args) {
        StringBuilder sb = new StringBuilder("Start");

        // 1. Appending (Chaining is possible)
        sb.append("-").append("Middle").append("-").append("End");

        // 2. Modifying
        sb.setCharAt(0, 's'); // "start-Middle-End"

        // 3. Deleting
        sb.delete(5, 12); // Removes "-Middle"

        // 4. Output
        System.out.println(sb.toString()); // "start-End"
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>The Winner:</strong> In modern Java development (like building your **organic e-commerce store**), 99% of the time you should use <code>StringBuilder</code>. <code>StringBuffer</code> is largely considered a legacy class unless you are explicitly working with complex multi-threaded legacy code.
             </p>
          </div>
        </div>
      );
      case 'StringBuffer':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java StringBuffer
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              <code>StringBuffer</code> is a thread-safe, mutable sequence of characters. It is used when you need to make many modifications to a string of characters (like in a loop) because it is more memory-efficient than <code>String</code>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Features */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Key Features</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Mutable:</strong> You can append, insert, or delete characters directly.</li>
                   <li>• <strong>Thread-Safe:</strong> All methods are synchronized, making it safe for use by multiple threads.</li>
                   <li>• <strong>Performance:</strong> Slower than <code>StringBuilder</code> but safer in multi-threaded environments.</li>
                </ul>
             </div>

             {/* Common Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Essential Methods</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>append(data)</code>: Adds data to the end of the buffer.</li>
                   <li>• <code>insert(index, data)</code>: Inserts data at a specific position.</li>
                   <li>• <code>reverse()</code>: Flips the sequence of characters.</li>
                   <li>• <code>delete(start, end)</code>: Removes a range of characters.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">StringBufferDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class StringBufferDemo {
    public static void main(String[] args) {
        // Create a StringBuffer with initial capacity
        StringBuffer sb = new StringBuffer("Java");

        // 1. Appending
        sb.append(" Programming"); // "Java Programming"

        // 2. Inserting
        sb.insert(5, "Backend "); // "Java Backend Programming"

        // 3. Reversing
        sb.reverse(); 

        // 4. Converting back to String
        String finalOutput = sb.toString();
        
        System.out.println(finalOutput);
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Interview Tip:</strong> If you are asked about the difference between <strong>StringBuffer</strong> and <strong>StringBuilder</strong>, remember that <code>StringBuffer</code> is <strong>synchronized</strong> (thread-safe) while <code>StringBuilder</code> is not. Use <code>StringBuilder</code> for single-threaded tasks to get better performance.
             </p>
          </div>
        </div>
      );
      case 'String Methods':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Common Java String Methods
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The <code>String</code> class in Java comes with a wealth of built-in methods. Since Strings are immutable, these methods do not change the original string but return a <strong>new string</strong> with the results.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Comparison & Search */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Comparison & Search</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>equals()</code>: Compares content (case-sensitive).</li>
                   <li>• <code>equalsIgnoreCase()</code>: Compares content (ignores case).</li>
                   <li>• <code>contains()</code>: Checks if a sequence exists.</li>
                   <li>• <code>indexOf()</code>: Returns the position of a character/substring.</li>
                </ul>
             </div>

             {/* Transformation */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Transformation</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>toUpperCase() / toLowerCase()</code>: Changes casing.</li>
                   <li>• <code>trim()</code>: Removes leading and trailing whitespace.</li>
                   <li>• <code>replace()</code>: Swaps characters or sequences.</li>
                   <li>• <code>substring(start, end)</code>: Extracts a portion of the string.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">StringMethodsDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class StringMethodsDemo {
    public static void main(String[] args) {
        String blog = "  Welcome to senvra.ai  ";

        // 1. Length and Trimming
        System.out.println("Length: " + blog.length()); // 24
        String cleanBlog = blog.trim(); // "Welcome to senvra.ai"

        // 2. Extraction
        String siteName = cleanBlog.substring(11); // "senvra.ai"

        // 3. Transformation
        System.out.println(cleanBlog.toUpperCase()); // "WELCOME TO SENVRA.AI"

        // 4. Checking Equality
        boolean isSame = cleanBlog.equals("Welcome to senvra.ai"); // true
        
        System.out.println("Contains 'ai'? " + cleanBlog.contains("ai")); // true
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Crucial Dev Rule:</strong> Never compare two Strings using <code>==</code>. The <code>==</code> operator compares <strong>memory addresses (references)</strong>. To compare the actual text inside the strings, always use the <code>.equals()</code> method.
             </p>
          </div>
        </div>
      );
      case 'Inheritance':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Inheritance in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Inheritance allows a <strong>Subclass</strong> to inherit features from a <strong>Superclass</strong> using the <code>extends</code> keyword. This promotes "DRY" (Don't Repeat Yourself) development.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Terminology */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Core Concepts</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Superclass (Parent):</strong> The class whose features are inherited.</li>
                   <li>• <strong>Subclass (Child):</strong> The class that inherits the other class.</li>
                   <li>• <strong>Reusability:</strong> You can use fields and methods of the parent without rewriting them.</li>
                </ul>
             </div>

             {/* Important Rule */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm border-l-4 border-l-orange-500">
                <h4 className="font-bold text-slate-800 dark:text-white mb-3 text-xs uppercase">The Single Inheritance Rule</h4>
                <p className="text-[11px] text-slate-500">
                   In Java, a class <strong>cannot</strong> extend more than one class directly. This prevents the "Diamond Problem." To achieve multiple inheritance behaviors, we use <strong>Interfaces</strong>.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">InheritanceDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// Parent Class
class User {
    String username;
    void login() {
        System.out.println(username + " logged in successfully.");
    }
}

// Child Class
class Admin extends User {
    void deleteUser() {
        System.out.println("Admin privileges: User deleted.");
    }
}

public class Main {
    public static void main(String[] args) {
        Admin myAdmin = new Admin();
        myAdmin.username = "Koteshwar"; // Inherited field
        myAdmin.login();                // Inherited method
        myAdmin.deleteUser();           // Own method
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>HRMS Context:</strong> In your HRMS system, you can have a parent class <code>Employee</code> with fields like <code>id</code> and <code>name</code>. Then, classes like <code>Developer</code> and <code>Manager</code> can <strong>extend</strong> <code>Employee</code>, inheriting those basic fields while adding their own specific logic.
             </p>
          </div>
        </div>
      );
      case 'Immutable vs Mutable':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Immutable vs. Mutable Objects
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              In Java, an <strong>Immutable</strong> object is one whose state cannot be modified after it is created. A <strong>Mutable</strong> object, however, allows you to change its content without creating a new instance.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Immutable Objects */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Immutable (e.g., String)</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Modification:</strong> Any change creates a <strong>new object</strong> in memory.</li>
                   <li>• <strong>Thread Safety:</strong> Inherently safe; multiple threads can't change the data.</li>
                   <li>• <strong>Memory:</strong> Uses the String Constant Pool for efficiency.</li>
                </ul>
             </div>

             {/* Mutable Objects */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Mutable (e.g., StringBuilder)</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Modification:</strong> Changes happen to the <strong>existing object</strong>.</li>
                   <li>• <strong>Efficiency:</strong> Better for heavy text manipulation (loops).</li>
                   <li>• <strong>Methods:</strong> Provides <code>append()</code>, <code>insert()</code>, and <code>delete()</code>.</li>
                </ul>
             </div>
          </div>

          [Image of a memory diagram comparing String immutability versus StringBuilder mutability showing how strings create new objects in the pool while StringBuilder modifies its internal buffer]

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">MutabilityDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class MutabilityDemo {
    public static void main(String[] args) {
        // IMMUTABLE: String
        String s = "Hello";
        s.concat(" World"); 
        // s still prints "Hello" because the result of concat was not saved!
        System.out.println("String: " + s); 

        // MUTABLE: StringBuilder
        StringBuilder sb = new StringBuilder("Hello");
        sb.append(" World"); 
        // sb is updated directly in memory
        System.out.println("StringBuilder: " + sb); 
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Performance Pro-Tip:</strong> If you are concatenating strings inside a loop (like building a large HTML report for your <strong>HRMS</strong>), never use a <code>String</code>. Use <code>StringBuilder</code> to avoid creating thousands of temporary objects that clog the garbage collector.
             </p>
          </div>
        </div>
      );
      case 'Method Declaration':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Method Declaration & Anatomy
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A method declaration provides the JVM with the "rules of engagement" for a block of code. Every part of the declaration serves a specific purpose, from security (modifiers) to data exchange (parameters).
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Parts of Declaration */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. The Method Header</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Modifier:</strong> (e.g., <code>public</code>) Defines accessibility.</li>
                   <li>• <strong>Return Type:</strong> The data type the method sends back.</li>
                   <li>• <strong>Method Name:</strong> Should be a verb in <code>camelCase</code>.</li>
                   <li>• <strong>Parameter List:</strong> Inputs required by the method.</li>
                </ul>
             </div>

             {/* The Method Body */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. The Method Body</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Everything contained within the curly braces <code>{`{ }`}</code>. This is where the actual logic of the method resides.
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded font-mono text-[9px] text-red-500">
                   {`{ // logic starts here ... }`}
                </div>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">MethodStructure.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class MethodStructure {

    // DECLARATION
    public int calculateArea(int length, int width) {
        // BODY
        int area = length * width;
        return area; 
    }

    public static void main(String[] args) {
        MethodStructure obj = new MethodStructure();
        // CALLING the method
        int result = obj.calculateArea(10, 20);
        System.out.println("Area: " + result);
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Naming Standard:</strong> In Java, method names should always start with a lowercase verb. For example, use <code>getUserData()</code> rather than <code>UserData()</code>. This follows the <strong>camelCase</strong> convention used throughout the industry.
             </p>
          </div>
        </div>
      );
      case 'Parameters / Arguments':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Method Parameters & Arguments
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Methods can act as "data processors." To do their job, they often need input. <strong>Parameters</strong> are the variables listed in the method definition, while <strong>Arguments</strong> are the actual values passed during the call.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Definitions */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Parameters (The Placeholder)</h4>
                <p className="text-[11px] text-slate-500">
                   Defined in the method signature. They act as local variables inside the method.
                </p>
                <code className="text-[9px] text-red-400 block mt-2">void myMethod(int x) // x is the parameter</code>
             </div>

             {/* Arguments */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Arguments (The Value)</h4>
                <p className="text-[11px] text-slate-500">
                   The real data you send to the method when you execute it.
                </p>
                <code className="text-[9px] text-orange-400 block mt-2">myMethod(25); // 25 is the argument</code>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">ParameterDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class ParameterDemo {

    // Method with multiple parameters
    static void displayUser(String name, int experience) {
        System.out.println("Developer: " + name);
        System.out.println("Years of Exp: " + experience);
    }

    public static void main(String[] args) {
        // Passing arguments
        displayUser("Koteshwar", 2); 
        
        // You can also pass variables as arguments
        String devName = "Chinnolla";
        int devExp = 5;
        displayUser(devName, devExp);
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Important Note:</strong> Java is strictly <strong>Pass-by-Value</strong>. This means when you pass a primitive variable as an argument, the method receives a <i>copy</i> of the value. Changing the parameter inside the method won't change the original variable in the <code>main</code> method.
             </p>
          </div>
        </div>
      );
      case 'Method Overloading':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Method Overloading
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Method Overloading allows you to define multiple methods with the same name but different signatures. This increases the readability of the program as it performs similar tasks with different inputs.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Rules for Overloading */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">How to Overload</h4>
                <p className="text-[11px] text-slate-500 mb-2">You can overload a method by changing:</p>
                <ul className="text-[10px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Number of parameters:</strong> add(int, int) vs add(int, int, int).</li>
                   <li>• <strong>Data types:</strong> add(int, int) vs add(double, double).</li>
                   <li>• <strong>Sequence of types:</strong> doTask(int, String) vs doTask(String, int).</li>
                </ul>
             </div>

             {/* Invalid Overloading */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm border-l-4 border-l-red-500">
                <h4 className="font-bold text-slate-800 dark:text-white mb-3 text-xs uppercase">Important Restriction</h4>
                <p className="text-[11px] text-slate-500">
                   You <strong>cannot</strong> overload a method by changing only its <strong>return type</strong>. The compiler distinguishes methods based on parameters, not what they return.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">OverloadDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class OverloadDemo {
    // Method 1: Two integer parameters
    static int multiply(int a, int b) {
        return a * b;
    }

    // Method 2: Three integer parameters (Overloaded)
    static int multiply(int a, int b, int c) {
        return a * b * c;
    }

    // Method 3: Two double parameters (Overloaded)
    static double multiply(double a, double b) {
        return a * b;
    }

    public static void main(String[] args) {
        System.out.println(multiply(5, 4));          // Calls Method 1
        System.out.println(multiply(5, 4, 2));       // Calls Method 2
        System.out.println(multiply(5.5, 2.0));      // Calls Method 3
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why use it?</strong> Overloading is useful when you want to perform the same operation on different data types. For example, in your <strong>travel booking platform</strong>, a <code>search()</code> method could be overloaded to search by <code>String destination</code> or by <code>int hotelID</code>.
             </p>
          </div>
        </div>
      );
      case 'Return Types':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Method Return Types
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A return type tells the compiler what kind of data to expect from a method. This can be a <strong>primitive type</strong> (like int or boolean), an <strong>object</strong> (like String), or <strong>void</strong> if no data is returned.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Void vs Value */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">The 'void' Keyword</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   When a method is declared as <code>void</code>, it performs an action (like printing to the console) but does not return a value. You cannot assign its result to a variable.
                </p>
             </div>

             {/* The Return Statement */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">The 'return' Statement</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   For non-void methods, the <code>return</code> keyword is mandatory. It stops the execution of the method and passes the value back to the caller.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">ReturnDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class ReturnDemo {
    // Return type: int
    static int calculateSum(int a, int b) {
        return a + b; // Value must be an int
    }

    // Return type: String
    static String getGreeting(String name) {
        return "Hello, " + name;
    }

    // Return type: void (No value returned)
    static void printLogo() {
        System.out.println("senvra.ai");
    }

    public static void main(String[] args) {
        int total = calculateSum(10, 20); // Result captured
        String msg = getGreeting("Koteshwar"); // Result captured
        
        printLogo(); // Just called, nothing to capture
    }
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Logic Trap:</strong> If your method claims to return an <code>int</code>, every possible execution path (including <code>if-else</code> blocks) <strong>must</strong> end with a return statement that provides an integer. Otherwise, the code won't compile.
             </p>
          </div>
        </div>
      );
      case 'Static vs Non-Static Methods':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Static vs. Non-Static Methods
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Understanding the <code>static</code> keyword is crucial for memory management. It determines if a method can be called directly using the class name or if it requires an object to be created first.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Static Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Static Methods</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Belongs to:</strong> The Class.</li>
                   <li>• <strong>Access:</strong> Called without creating an object.</li>
                   <li>• <strong>Memory:</strong> Loaded once when the class is loaded.</li>
                   <li>• <strong>Example:</strong> <code>Math.sqrt()</code>.</li>
                </ul>
             </div>

             {/* Non-Static Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Non-Static (Instance)</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Belongs to:</strong> The Object.</li>
                   <li>• <strong>Access:</strong> Must create an instance (using <code>new</code>).</li>
                   <li>• <strong>State:</strong> Can access instance variables (fields).</li>
                   <li>• <strong>Example:</strong> <code>scanner.nextLine()</code>.</li>
                </ul>
             </div>
          </div>

          [Image of a diagram comparing static and non-static memory allocation in Java showing Method Area vs Heap]

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">MethodComparison.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Calculator {
    // Static Method: Utility behavior
    static int add(int a, int b) {
        return a + b;
    }

    // Non-Static Method: Instance behavior
    void displayResult(int res) {
        System.out.println("Result is: " + res);
    }

    public static void main(String[] args) {
        // Calling Static Method (No object needed)
        int sum = Calculator.add(5, 10);

        // Calling Non-Static Method (Object REQUIRED)
        Calculator myCalc = new Calculator();
        myCalc.displayResult(sum);
    }
}`}
             </pre>
          </div>

          [Image of a table comparing static and instance methods in Java across categories like memory, access, and keywords]

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>The Main Rule:</strong> A <code>static</code> method cannot call a <code>non-static</code> method directly. This is because the static method exists before any objects are created, so it wouldn't know which object's method to run!
             </p>
          </div>
        </div>
      );
      case 'Recursion':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Recursion in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Recursion breaks a large problem into smaller, similar sub-problems. Every recursive function must have a condition to stop calling itself; otherwise, it will run forever and cause a <strong>StackOverflowError</strong>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The Two Essentials */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">The Anatomy of Recursion</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Base Case:</strong> The condition that stops the recursion (the "exit").</li>
                   <li>• <strong>Recursive Case:</strong> The part where the method calls itself with a modified (usually smaller) input.</li>
                </ul>
             </div>

             {/* Memory Management */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">The Stack Memory</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Each recursive call is pushed onto the <strong>Stack</strong>. When the base case is reached, the calls are popped off one by one.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">Factorial.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Factorial {
    // Recursive method to find factorial of n
    static int findFactorial(int n) {
        // 1. Base Case
        if (n == 1) {
            return 1;
        }
        // 2. Recursive Case
        else {
            return n * findFactorial(n - 1);
        }
    }

    public static void main(String[] args) {
        int result = findFactorial(5);
        System.out.println("Factorial of 5 is: " + result); // 120
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>DSA Insight:</strong> Recursion is the backbone of advanced algorithms like <strong>QuickSort</strong>, <strong>MergeSort</strong>, and <strong>Tree Traversals</strong>. While elegant, remember that it consumes more memory than loops due to the overhead of maintaining the call stack.
             </p>
          </div>
        </div>
      );
      case 'Classes & Objects':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Classes & Objects
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              In Java, everything is associated with classes and objects. A <strong>Class</strong> is a blueprint for creating objects, and an <strong>Object</strong> is an instance of a class that contains real values.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The Class */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">The Class (Blueprint)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   A class defines the <strong>Attributes</strong> (variables) and <strong>Behaviors</strong> (methods) that its objects will have. It doesn't consume memory until an object is created.
                </p>
             </div>

             {/* The Object */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">The Object (Instance)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   An object is a specific "thing" created from a class. It has its own unique state stored in memory (the Heap).
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">Main.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// The Class
class Employee {
    String name; // Attribute
    int id;      // Attribute

    void work() { // Behavior
        System.out.println(name + " is coding...");
    }
}

public class Main {
    public static void main(String[] args) {
        // Creating an Object (Instance)
        Employee dev = new Employee();
        
        // Accessing attributes and methods
        dev.name = "Koteshwar";
        dev.id = 101;
        dev.work(); 
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Memory Note:</strong> When you declare <code>Employee dev</code>, a reference is created on the <strong>Stack</strong>. When you use <code>new Employee()</code>, the actual object is created on the <strong>Heap</strong>.
             </p>
          </div>
        </div>
      );
      case 'Constructors':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Constructors
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Every time you use the <code>new</code> keyword in Java, you are calling a constructor. If you don't write one yourself, Java automatically provides a "Default Constructor" for you.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Types of Constructors */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Default / No-Arg</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Takes no arguments. It initializes instance variables with default values (0, null, false).
                </p>
             </div>

             {/* Parameterized Constructors */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Parameterized</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Allows you to pass specific values at the time of object creation, making your objects flexible.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">Product.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Product {
    String name;
    double price;

    // Parameterized Constructor
    public Product(String n, double p) {
        this.name = n;
        this.price = p;
    }

    public static void main(String[] args) {
        // Calling the constructor with arguments
        Product p1 = new Product("Organic Apple", 2.50);
        System.out.println(p1.name + " costs $" + p1.price);
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Pro Tip:</strong> In your <strong>JJROrganic</strong> project, you can use constructors to ensure every <code>Order</code> object is created with a mandatory <code>CustomerID</code> and <code>Date</code>. This prevents the creation of "empty" or invalid orders in your database.
             </p>
          </div>
        </div>
      );
      case 'Polymorphism':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Polymorphism in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Polymorphism occurs when there is a hierarchy of classes related to each other by inheritance. It allows a parent class reference to point to a child class object, enabling "plug-and-play" code architecture.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Compile-time Polymorphism */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Static (Compile-time)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Achieved through <strong>Method Overloading</strong>. The compiler determines which method to call based on the parameter list.
                </p>
             </div>

             {/* Runtime Polymorphism */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Dynamic (Runtime)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Achieved through <strong>Method Overriding</strong>. The JVM determines which method to call at runtime based on the actual object type.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">PolymorphismDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`class Payment {
    void process() { System.out.println("Processing payment..."); }
}

class UPI extends Payment {
    @Override
    void process() { System.out.println("Processing via PhonePe/GPay..."); }
}

class Card extends Payment {
    @Override
    void process() { System.out.println("Processing via Credit Card..."); }
}

public class Main {
    public static void main(String[] args) {
        // Parent reference, Child object (Upcasting)
        Payment myPay = new UPI();
        myPay.process(); // Outputs: Processing via PhonePe/GPay...

        myPay = new Card();
        myPay.process(); // Outputs: Processing via Credit Card...
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Perspective:</strong> Polymorphism is why you can have a list of <code>Shape</code> objects in your <strong>travel booking app</strong> and call <code>.calculatePrice()</code> on all of them, regardless of whether they are <code>Flight</code>, <code>Hotel</code>, or <code>Bus</code> bookings.
             </p>
          </div>
        </div>
      );
      case 'Encapsulation':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Encapsulation (Data Hiding)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Encapsulation acts as a protective shield that prevents the data from being accessed by the code outside this shield. You achieve this by declaring variables as <strong>private</strong> and providing <strong>getters and setters</strong>.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The Mechanism */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">How to Encapsulate</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Private Fields:</strong> Declare class variables as <code>private</code>.</li>
                   <li>• <strong>Public Methods:</strong> Provide <code>get</code> and <code>set</code> methods to view or modify values.</li>
                   <li>• <strong>Validation:</strong> Add logic inside setters to prevent invalid data.</li>
                </ul>
             </div>

             {/* The Benefits */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Benefits</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   It provides <strong>security</strong> and makes the code <strong>flexible</strong>. You can make a class "read-only" by omitting the setter methods.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">EmployeeAccount.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Employee {
    // 1. Private data
    private double salary;

    // 2. Getter (Accessor)
    public double getSalary() {
        return salary;
    }

    // 3. Setter (Mutator) with Validation
    public void setSalary(double newSalary) {
        if (newSalary > 0) {
            this.salary = newSalary;
        } else {
            System.out.println("Invalid salary amount!");
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Real-World Usage:</strong> In your <strong>HRMS system</strong>, you wouldn't want any part of the app to directly change an employee's <code>leaveBalance</code>. By using encapsulation, you can ensure that the balance only decreases when a leave request is officially approved through a <code>setLeave()</code> method.
             </p>
          </div>
        </div>
      );
      case 'Encapsulation':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Encapsulation (Data Hiding)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Encapsulation acts as a protective shield that prevents the data from being accessed by the code outside this shield. You achieve this by declaring variables as <strong>private</strong> and providing <strong>getters and setters</strong>.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The Mechanism */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">How to Encapsulate</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Private Fields:</strong> Declare class variables as <code>private</code>.</li>
                   <li>• <strong>Public Methods:</strong> Provide <code>get</code> and <code>set</code> methods to view or modify values.</li>
                   <li>• <strong>Validation:</strong> Add logic inside setters to prevent invalid data.</li>
                </ul>
             </div>

             {/* The Benefits */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Benefits</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   It provides <strong>security</strong> and makes the code <strong>flexible</strong>. You can make a class "read-only" by omitting the setter methods.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">EmployeeAccount.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Employee {
    // 1. Private data
    private double salary;

    // 2. Getter (Accessor)
    public double getSalary() {
        return salary;
    }

    // 3. Setter (Mutator) with Validation
    public void setSalary(double newSalary) {
        if (newSalary > 0) {
            this.salary = newSalary;
        } else {
            System.out.println("Invalid salary amount!");
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Real-World Usage:</strong> In your <strong>HRMS system</strong>, you wouldn't want any part of the app to directly change an employee's <code>leaveBalance</code>. By using encapsulation, you can ensure that the balance only decreases when a leave request is officially approved through a <code>setLeave()</code> method.
             </p>
          </div>
        </div>
      );
      case 'Interfaces':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Interfaces in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              An interface is a formal contract. When a class <strong>implements</strong> an interface, it promises to provide specific behavior. This allows different classes to be treated the same way if they share the same interface.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Characteristics */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Rules of Interfaces</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Methods:</strong> By default, all methods are <code>public</code> and <code>abstract</code>.</li>
                   <li>• <strong>Variables:</strong> All variables are <code>public static final</code> (constants).</li>
                   <li>• <strong>Multiple Inheritance:</strong> A class can implement <strong>multiple</strong> interfaces.</li>
                   <li>• <strong>No State:</strong> They cannot contain instance fields or constructors.</li>
                </ul>
             </div>

             {/* Interface vs Abstract Class */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Why use them?</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Use interfaces to define a <strong>capability</strong> (like "Sortable" or "Printable") that can be shared across unrelated classes.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">PaymentSystem.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// 1. Defining the Interface
interface PaymentGateway {
    void processPayment(double amount); // Abstract method
}

// 2. Implementation: PayPal
class PayPal implements PaymentGateway {
    public void processPayment(double amount) {
        System.out.println("Processing PayPal payment of $" + amount);
    }
}

// 3. Implementation: Credit Card
class CreditCard implements PaymentGateway {
    public void processPayment(double amount) {
        System.out.println("Charging Credit Card: $" + amount);
    }
}

public class Main {
    public static void main(String[] args) {
        PaymentGateway myPay = new PayPal(); 
        myPay.processPayment(500.0);
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Project Application:</strong> For your <strong>organic e-commerce store</strong>, you might have an interface called <code>Discountable</code>. Both a <code>Product</code> and a <code>Service</code> could implement it, even though they are completely different types of objects, ensuring both have a <code>applyDiscount()</code> method.
             </p>
          </div>
        </div>
      );
      case 'Abstract Classes':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Abstract Classes in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Abstraction is the process of hiding certain details and showing only essential information to the user. An abstract class acts as a template that enforces a specific contract for all its children.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Rules */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Essential Rules</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>No Instantiation:</strong> You cannot create an object using <code>new AbstractClass()</code>.</li>
                   <li>• <strong>Abstract Methods:</strong> Methods without a body (logic) that <strong>must</strong> be overridden by subclasses.</li>
                   <li>• <strong>Concrete Methods:</strong> Can also have regular methods with a full body.</li>
                </ul>
             </div>

             {/* Why Use Abstraction */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">The Purpose</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Use an abstract class when you want to share code among several closely related classes but want to force them to implement their own version of certain behaviors.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">AbstractionDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// 1. Abstract Class
abstract class Employee {
    String name;
    
    // Abstract method (no implementation)
    abstract double calculateSalary();

    // Concrete method (standard implementation)
    void showDetails() {
        System.out.println("Employee Name: " + name);
    }
}

// 2. Subclass providing implementation
class Developer extends Employee {
    Developer(String name) { this.name = name; }

    @Override
    double calculateSalary() {
        return 80000.00;
    }
}

public class Main {
    public static void main(String[] args) {
        // Employee emp = new Employee(); // ERROR: Cannot instantiate
        Developer dev = new Developer("Koteshwar");
        dev.showDetails();
        System.out.println("Salary: " + dev.calculateSalary());
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>HRMS Use Case:</strong> In your HRMS system, <code>Employee</code> could be an abstract class. While all employees have a <code>name</code> and <code>ID</code>, the way you <code>calculateSalary()</code> is different for a <code>FullTimeEmployee</code> versus a <code>Contractor</code>. Abstraction ensures every employee type has a salary calculation without defining a "default" one that might be wrong.
             </p>
          </div>
        </div>
      );
      case 'Abstraction':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Abstraction in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Abstraction reduces complexity by hiding unnecessary details. Think of a TV remote: you know that pressing the "Power" button turns the TV on, but you don't need to understand the internal circuitry to use it.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Abstract Classes */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Abstract Classes (0-100%)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Used when you want to provide a common base with some shared logic, but force subclasses to implement specific methods.
                </p>
                <code className="text-[9px] text-red-400 font-mono">abstract class Vehicle {"{ ... }"}</code>
             </div>

             {/* Interfaces */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Interfaces (100%)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   The ultimate form of abstraction. It defines a contract of behavior that any implementing class must follow.
                </p>
                <code className="text-[9px] text-orange-400 font-mono">interface GPS {"{ ... }"}</code>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">AbstractionInAction.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// Abstract Class: Focuses on "What is it?"
abstract class BankAccount {
    // Abstract method: Hides the "How"
    abstract void applyInterest();

    // Concrete method: Shared detail
    void deposit(double amount) {
        System.out.println("Deposited: " + amount);
    }
}

class SavingsAccount extends BankAccount {
    @Override
    void applyInterest() {
        // Specific implementation for Savings
        System.out.println("Interest applied at 4%.");
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why it matters:</strong> Abstraction allows you to change the underlying logic (like switching a database or an algorithm) without affecting the code that uses your classes. It makes your system modular and easier to scale.
             </p>
          </div>
        </div>
      );
      case 'Access Modifiers':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Access Modifiers
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Access modifiers define the scope of a class member. By controlling who can see or modify your data, you ensure that your code remains secure and easy to maintain.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Public & Private */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Public vs. Private</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>public:</strong> Accessible from anywhere in the Java world.</li>
                   <li>• <strong>private:</strong> Accessible <strong>only</strong> within the same class. Best for sensitive data.</li>
                </ul>
             </div>

             {/* Protected & Default */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Protected & Default</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>protected:</strong> Accessible in the same package and by subclasses.</li>
                   <li>• <strong>Default (No keyword):</strong> Accessible only within the same package.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">AccessDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Account {
    public String bankName = "Senvra Bank";
    private double balance = 5000.0; // Hidden from other classes

    // Getter method to provide controlled access
    public double getBalance() {
        // You could add authentication logic here
        return this.balance;
    }

    protected void alertInternalSystems() {
        System.out.println("Processing transaction...");
    }
}

class Branch extends Account {
    void check() {
        System.out.println(bankName); // Allowed (public)
        alertInternalSystems();      // Allowed (protected/inheritance)
        // System.out.println(balance); // ERROR: balance is private
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Best Practice:</strong> Always follow the "Principle of Least Privilege." Start by making all variables <code>private</code> and only provide access through <code>public</code> getters and setters if absolutely necessary. This prevents accidental data corruption in large projects.
             </p>
          </div>
        </div>
      );
      case 'this & super':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              The 'this' and 'super' Keywords
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              These keywords are used to resolve ambiguity in your code. While <strong>this</strong> refers to the current object, <strong>super</strong> refers to the immediate parent object.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The 'this' Keyword */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">this Keyword</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Instance Variables:</strong> Used to distinguish between class fields and method parameters with the same name.</li>
                   <li>• <strong>Constructor Chaining:</strong> Used to call one constructor from another in the same class.</li>
                </ul>
             </div>

             {/* The 'super' Keyword */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">super Keyword</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Parent Access:</strong> Used to call parent class methods or variables that have been overridden.</li>
                   <li>• <strong>Parent Constructor:</strong> Invokes the parent's constructor (must be the first line).</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">InheritanceRef.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`class Parent {
    String role = "Manager";
    Parent() { System.out.println("Parent Constructor"); }
}

class Child extends Parent {
    String role = "Developer"; // Shadowing parent variable

    Child() {
        super(); // 1. Invoking Parent Constructor
        System.out.println("Child Constructor");
    }

    void display() {
        // 2. Using 'this' for current class
        System.out.println("Local Role: " + this.role); 
        
        // 3. Using 'super' for parent class
        System.out.println("Parent Role: " + super.role);
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Logic Note:</strong> You cannot use <code>this()</code> and <code>super()</code> in the same constructor. Both must be the first statement in a constructor, which makes using them together impossible.
             </p>
          </div>
        </div>
      );
      case 'Packages':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Packages & Organization
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              As your projects grow, keeping all files in one place becomes impossible. Packages provide a way to categorize your code, provide access protection, and manage the <strong>Namespace</strong>.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Types of Packages */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Built-in Packages</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Packages provided by the Java API, such as:
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• <code>java.lang</code>: Fundamental classes (auto-imported).</li>
                   <li>• <code>java.util</code>: Utility classes like Scanner and Arrays.</li>
                   <li>• <code>java.io</code>: Classes for input and output operations.</li>
                </ul>
             </div>

             {/* User-Defined Packages */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. User-Defined</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Packages you create to organize your own modules.
                </p>
                <code className="text-[9px] block bg-slate-100 dark:bg-slate-800 p-2 rounded text-red-500">
                   package com.senvra.hrms;
                </code>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">Directory Structure</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// File: src/com/senvra/auth/Login.java
package com.senvra.auth; // Must be the first line

public class Login {
    public void authenticate() {
        System.out.println("User Logged In");
    }
}

// File: src/Main.java
import com.senvra.auth.Login; // Importing the class

public class Main {
    public static void main(String[] args) {
        Login auth = new Login();
        auth.authenticate();
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Naming Convention:</strong> Use your domain name in reverse to ensure uniqueness globally. For your projects, you might use <code>com.anasol.hrms</code> for the HR system or <code>com.jjrorganic.store</code> for your e-commerce platform.
             </p>
          </div>
        </div>
      );
      case 'Exception Hierarchy':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              The Java Exception Hierarchy
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Java organizes exceptions into a clear parent-child relationship. This allows you to catch specific errors (like <code>FileNotFoundException</code>) or group them under a general parent (like <code>IOException</code>).
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Checked Exceptions */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Checked (Compile-time)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   These are checked at compile-time. Java forces you to handle them using try-catch or declare them with <code>throws</code>.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400 italic">
                   <li>• IOException</li>
                   <li>• SQLException</li>
                   <li>• ClassNotFoundException</li>
                </ul>
             </div>

             {/* Unchecked Exceptions */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Unchecked (Runtime)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   These occur during program execution due to logical flaws. They extend <code>RuntimeException</code>.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400 italic">
                   <li>• NullPointerException</li>
                   <li>• ArithmeticException</li>
                   <li>• ArrayIndexOutOfBoundsException</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">HierarchyContext.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class HierarchyDemo {
    public static void main(String[] args) {
        try {
            // NullPointerException is a child of RuntimeException
            String text = null;
            System.out.println(text.length()); 
        } 
        catch (RuntimeException e) { 
            // Catching the parent handles all its children
            System.out.println("Caught a Runtime Exception: " + e);
        }
        catch (Exception e) {
            // This is the broader parent of RuntimeException
            System.out.println("Caught a general Exception");
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Important Note:</strong> Never try to catch <code>Error</code> (like <code>OutOfMemoryError</code> or <code>StackOverflowError</code>). These represent fatal JVM issues that your code cannot recover from; instead, focus your <code>try-catch</code> blocks on the <code>Exception</code> branch.
             </p>
          </div>
        </div>
      );
      case 'Custom Exceptions':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Custom Exceptions
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Sometimes the standard Java exceptions don't describe a problem well enough. Custom exceptions allow you to define error types that make sense for your specific project requirements.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* How to Create */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. The Creation</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Inheritance:</strong> Your class must extend <code>Exception</code> (for checked) or <code>RuntimeException</code> (for unchecked).</li>
                   <li>• <strong>Constructor:</strong> Typically includes a constructor that passes a message to the <code>super</code> class.</li>
                </ul>
             </div>

             {/* Why use them? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. The Benefit</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   They improve <strong>readability</strong> and <strong>debugging</strong>. When an error occurs, the stack trace will show your specific class name, making the source of the bug obvious.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">StockException.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// 1. Defining the Custom Exception
class OutOfStockException extends Exception {
    public OutOfStockException(String message) {
        super(message);
    }
}

// 2. Using the Exception in your Organic Store project
class Inventory {
    void purchase(int quantity) throws OutOfStockException {
        if (quantity > 10) {
            throw new OutOfStockException("Only 10 items left in stock!");
        }
        System.out.println("Purchase successful!");
    }
}

public class Main {
    public static void main(String[] args) {
        Inventory shop = new Inventory();
        try {
            shop.purchase(15);
        } catch (OutOfStockException e) {
            System.out.println("Alert: " + e.getMessage());
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Project Application:</strong> In your **HRMS application**, you could create an <code>InvalidLeaveRequestException</code>. If an employee tries to request more days than their <code>leaveBalance</code> allows, you can throw this specific exception to trigger a UI alert in your **React Native** frontend.
             </p>
          </div>
        </div>
      );
      case 'throw / throws':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Throw vs. Throws
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              In Java, <strong>throw</strong> is an action (doing), while <strong>throws</strong> is a declaration (warning). Using them correctly ensures that your application handles errors predictably rather than crashing unexpectedly.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The throw Keyword */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. throw (The Action)</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Purpose:</strong> Used to explicitly throw a single exception.</li>
                   <li>• <strong>Location:</strong> Used inside a method or block of code.</li>
                   <li>• <strong>Syntax:</strong> Followed by an <code>instance</code> of an exception class (e.g., <code>new Exception()</code>).</li>
                </ul>
             </div>

             {/* The throws Keyword */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. throws (The Warning)</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Purpose:</strong> Declares that a method might throw one or more exceptions.</li>
                   <li>• <strong>Location:</strong> Used in the method signature.</li>
                   <li>• <strong>Syntax:</strong> Followed by exception <code>class names</code> (e.g., <code>IOException, SQLException</code>).</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">ValidationService.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Validator {
    // 'throws' warns the caller they must handle this error
    void checkAge(int age) throws ArithmeticException {
        if (age < 18) {
            // 'throw' actually triggers the error
            throw new ArithmeticException("Access denied - Under 18");
        } else {
            System.out.println("Access granted.");
        }
    }

    public static void main(String[] args) {
        Validator obj = new Validator();
        try {
            obj.checkAge(15);
        } catch (ArithmeticException e) {
            System.out.println("Caught: " + e.getMessage());
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Logic Note:</strong> When you use <code>throws</code> for a "Checked Exception" (like <code>IOException</code>), the person calling your method is forced by the compiler to either use a <code>try-catch</code> block or declare the exception themselves. This ensures that critical errors are never ignored.
             </p>
          </div>
        </div>
      );
      case 'try / catch / finally':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Exception Handling: Try-Catch-Finally
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Exceptions are events that disrupt the normal flow of a program. Using these blocks allows you to "catch" the error, handle it gracefully, and clean up resources afterward.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {/* Try Block */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. The Try Block</h4>
                <p className="text-[11px] text-slate-500">
                   Contains the code that <strong>might</strong> throw an exception. It must be followed by either catch or finally.
                </p>
             </div>

             {/* Catch Block */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. The Catch Block</h4>
                <p className="text-[11px] text-slate-500">
                   Used to <strong>handle</strong> the exception. It only executes if an error occurs in the try block.
                </p>
             </div>

             {/* Finally Block */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-yellow-600 mb-3 text-xs uppercase italic">3. The Finally Block</h4>
                <p className="text-[11px] text-slate-500">
                   <strong>Always</strong> executes, regardless of whether an exception was thrown or caught. Perfect for cleanup.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">ExceptionDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Main {
    public static void main(String[] args) {
        try {
            int data = 100 / 0; // Throws ArithmeticException
            System.out.println("This will not execute.");
        } 
        catch (ArithmeticException e) {
            System.out.println("Error: Cannot divide by zero!");
        } 
        finally {
            System.out.println("Finally block: Closing resources...");
        }
        System.out.println("Rest of the code continues...");
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>HRMS Use Case:</strong> When fetching employee records in your <strong>HRMS</strong>, use <code>try-catch</code> to handle potential <code>SQLException</code> errors. The <code>finally</code> block should be used to close your database connection, ensuring you don't leak memory even if the query fails.
             </p>
          </div>
        </div>
      );
      case 'Object class methods':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              The Object Class Methods
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Since <code>java.lang.Object</code> is the parent of all classes, its methods are available to every object in Java. These methods define how objects are compared, stringified, and synchronized.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Common Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Frequently Overridden</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>toString()</code>: Returns a string representation of the object.</li>
                   <li>• <code>equals(Object obj)</code>: Compares two objects for equality based on values.</li>
                   <li>• <code>hashCode()</code>: Returns an integer value for memory address or unique identification.</li>
                </ul>
             </div>

             {/* Lifecycle & Threading */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">System & Threading</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>getClass()</code>: Returns the runtime class of the object.</li>
                   <li>• <code>clone()</code>: Creates and returns a copy of the object.</li>
                   <li>• <code>wait(), notify(), notifyAll()</code>: Used for inter-thread communication.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">ObjectMethodsDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`class Employee {
    String name;
    Employee(String name) { this.name = name; }

    // Overriding toString to provide meaningful info
    @Override
    public String toString() {
        return "Employee Name: " + this.name;
    }
}

public class Main {
    public static void main(String[] args) {
        Employee e1 = new Employee("Ajay");
        
        // 1. Using toString()
        System.out.println(e1.toString()); 

        // 2. Using getClass()
        System.out.println("Class Type: " + e1.getClass().getName());
        
        // 3. Using hashCode()
        System.out.println("Memory ID: " + e1.hashCode());
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Developer Insight:</strong> In your <strong>HRMS system</strong>, you should always override <code>equals()</code> and <code>hashCode()</code> for your <code>Employee</code> class. This ensures that if you have two different object instances with the same Employee ID, Java treats them as the same person when stored in a Set or Map.
             </p>
          </div>
        </div>
      );
      case 'annotations':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Annotations
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Annotations act as tags that provide extra information to the Java compiler or the JVM. They don't change the execution of the code directly but can be used by tools to generate code, perform checks, or manage configurations.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Built-in Annotations */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. General Annotations</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>@Override</code>: Ensures a method is correctly overriding a parent method.</li>
                   <li>• <code>@Deprecated</code>: Marks a method as outdated and warns other developers not to use it.</li>
                   <li>• <code>@SuppressWarnings</code>: Tells the compiler to ignore specific warnings in a block of code.</li>
                </ul>
             </div>

             {/* Meta-Annotations */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Meta-Annotations</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   These are annotations used to define other annotations:
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• <code>@Target</code>: Defines where the annotation can be applied (e.g., Method, Field).</li>
                   <li>• <code>@Retention</code>: Defines how long the annotation is kept (Source, Class, or Runtime).</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">AnnotationExample.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`class Parent {
    void display() { System.out.println("Parent Class"); }
}

class Child extends Parent {
    @Override // Compiler check: Does this exist in Parent?
    void display() { 
        System.out.println("Child Class"); 
    }

    @Deprecated // Warning: This method is old
    void oldMethod() {
        System.out.println("Don't use me!");
    }
}

public class Main {
    @SuppressWarnings("unchecked") // Ignore type-safety warnings here
    public static void main(String[] args) {
        Child c = new Child();
        c.display();
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Project Context:</strong> In your <strong>HRMS</strong> or <strong>LMS project</strong>, you might use annotations for database mapping (like JPA's <code>@Entity</code> or <code>@Table</code>). This allows the system to automatically link your Java classes to your SQL tables without manually writing every query.
             </p>
          </div>
        </div>
      );
      case 'enums':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Enums
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              An Enum is a powerful tool for defining a collection of related constants. Unlike C/C++ enums, Java enums are full-featured classes that can have fields, methods, and even constructors.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Features */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Enum Characteristics</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Type Safety:</strong> You cannot assign any value other than the defined constants.</li>
                   <li>• <strong>Singleton:</strong> Each enum constant is a public static final instance of the enum class.</li>
                   <li>• <strong>Built-in Methods:</strong> Includes <code>values()</code> to loop through constants and <code>ordinal()</code> for their position.</li>
                </ul>
             </div>

             {/* Why use Enums? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">The Advantage</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Enums eliminate "magic strings" and "magic numbers" in your code, reducing bugs caused by typos in status names or category types.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">OrderStatus.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`enum Status {
    PENDING,
    SHIPPED,
    DELIVERED,
    CANCELLED
}

public class Main {
    public static void main(String[] args) {
        Status myOrder = Status.SHIPPED;

        // Using Enum in a Switch statement
        switch(myOrder) {
            case PENDING: System.out.println("Order is being prepared."); break;
            case SHIPPED: System.out.println("Order is on the way!"); break;
            case DELIVERED: System.out.println("Order has arrived."); break;
        }

        // Looping through all constants
        for (Status s : Status.values()) {
            System.out.println(s);
        }
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>HRMS Integration:</strong> In your <strong>HRMS application</strong>, you could use an Enum called <code>LeaveType</code> with values like <code>SICK_LEAVE</code>, <code>CASUAL_LEAVE</code>, and <code>MATERNITY_LEAVE</code>. This ensures that when an employee requests leave, the system only accepts valid, predefined categories.
             </p>
          </div>
        </div>
      );
      case 'anonymous inner class':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Anonymous Inner Classes
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              An anonymous inner class is a type of inner class that is declared and instantiated at the same time. It is useful when you want to provide a specific implementation for a single instance of a class or interface.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Characteristics */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Characteristics</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>No Name:</strong> It has no name, so it cannot have a constructor.</li>
                   <li>• <strong>One-time Use:</strong> It is used to create only one object.</li>
                   <li>• <strong>Scope:</strong> It can access the members of its enclosing class and local variables that are final or effectively final.</li>
                </ul>
             </div>

             {/* When to Use */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Best Use Cases</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Perfect for <strong>event handling</strong>, <strong>thread creation</strong>, or implementing <strong>Functional Interfaces</strong> where a full class would be overkill.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">AnonymousDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`interface Greeting {
    void sayHello();
}

public class Main {
    public static void main(String[] args) {
        // Anonymous Inner Class implementing Greeting interface
        Greeting g = new Greeting() {
            @Override
            public void sayHello() {
                System.out.println("Hello from the Anonymous Class!");
            }
        };

        g.sayHello();
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Modern Java Note:</strong> In modern Java (8+), anonymous inner classes used for interfaces with a single method are often replaced by <strong>Lambda Expressions</strong>. However, for classes with multiple methods, anonymous inner classes are still the go-to solution.
             </p>
          </div>
        </div>
      );
      case 'inner classes':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Inner & Nested Classes
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              An inner class is a member of its enclosing class. It has access to the members of the outer class, including those declared as <strong>private</strong>.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Member Inner Class */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Member Inner Class</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   A non-static class created inside a class but outside a method. It requires an instance of the outer class to exist.
                </p>
             </div>

             {/* Static Nested Class */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Static Nested Class</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Declared with the <code>static</code> keyword. It can be instantiated without an outer class instance but cannot access non-static outer members.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">OuterClass.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`class University {
    private String uniName = "Senvra Global";

    // Inner Class
    class Department {
        void display() {
            // Accessing private member of outer class
            System.out.println("Welcome to " + uniName);
        }
    }

    public static void main(String[] args) {
        University myUni = new University();
        // Creating inner class object requires outer object
        University.Department myDept = myUni.new Department();
        myDept.display();
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Advanced Note:</strong> You can also use <strong>Anonymous Inner Classes</strong>, which are inner classes without a name. These are frequently used in your <strong>React</strong> or <strong>React Native</strong> development patterns for handling one-time events or listeners.
             </p>
          </div>
        </div>
      );
      case 'static keyword':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              The 'static' Keyword
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              When a member is declared <code>static</code>, it can be accessed before any objects of its class are created, and without reference to any object. It is shared by all instances of the class.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Static Variables */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Static Variables</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Also known as <strong>Class Variables</strong>. Only one copy exists in memory, regardless of how many objects you create.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• Ideal for common properties (e.g., Company Name).</li>
                   <li>• Saves memory by avoiding duplication.</li>
                </ul>
             </div>

             {/* Static Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Static Methods</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Methods that can be called using the Class Name. They can <strong>only</strong> access other static data directly.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• Common for utility/helper functions (e.g., <code>Math.pow()</code>).</li>
                   <li>• Cannot use <code>this</code> or <code>super</code> keywords.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">StaticTest.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class Student {
    String name; // Instance variable
    static String college = "Senvra Institute"; // Static variable

    Student(String n) {
        this.name = n;
    }

    static void changeCollege(String newCollege) {
        college = newCollege; // Static method changing static data
        // name = "Error"; // ERROR: Static method cannot access non-static field
    }

    void display() {
        System.out.println(name + " studies at " + college);
    }

    public static void main(String[] args) {
        Student s1 = new Student("Ajay");
        Student s2 = new Student("Mahanth");

        s1.display();
        Student.changeCollege("Tech University");
        s2.display(); // Both students now show "Tech University"
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why it matters:</strong> In your <strong>Poultry ERP system</strong>, you might use a <code>static</code> counter to keep track of the total number of batches created across the entire system. Since it's a global count, you don't want each batch object to have its own independent number.
             </p>
          </div>
        </div>
      );
      case 'final keyword':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              The 'final' Keyword
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The <code>final</code> keyword acts as a "seal." Once a value or logic is marked as final, it cannot be modified, overridden, or inherited. This provides security and performance optimizations for the JVM.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {/* Final Variables */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Variables</h4>
                <p className="text-[11px] text-slate-500">
                   Creates a <strong>constant</strong>. Once assigned, the value cannot be changed.
                </p>
             </div>

             {/* Final Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Methods</h4>
                <p className="text-[11px] text-slate-500">
                   Prevents <strong>Method Overriding</strong>. Subclasses cannot change the method's logic.
                </p>
             </div>

             {/* Final Classes */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-yellow-600 mb-3 text-xs uppercase italic">3. Classes</h4>
                <p className="text-[11px] text-slate-500">
                   Prevents <strong>Inheritance</strong>. No other class can extend a final class.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">FinalDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`final class SecurityConfig {
    // 1. Final Variable
    final double MAX_LOGIN_ATTEMPTS = 5;

    // 2. Final Method
    final void validateToken() {
        System.out.println("Token validated.");
    }

    void changeAttempts() {
        // MAX_LOGIN_ATTEMPTS = 10; // ERROR: Cannot assign a value to final variable
    }
}

// class Hacker extends SecurityConfig { } // ERROR: Cannot inherit from final class`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Tip:</strong> In your <strong>HRMS</strong> or <strong>API Admin Portal</strong>, use <code>final</code> for sensitive configurations like API keys or base URLs. Also, the <code>String</code> class in Java is <code>final</code>, which is why you can't create a class that extends String.
             </p>
          </div>
        </div>
      );
      case 'Wildcards':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Generics: Wildcards (?)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Wildcards resolve the issue where a <code>List&lt;Integer&gt;</code> is NOT considered a subtype of <code>List&lt;Number&gt;</code>. They allow you to define flexible bounds for reading from or writing to generic collections.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {/* Upper Bounded */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Upper Bounded</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Syntax: <code>? extends T</code>. Accepts T or any subclass of T. 
                </p>
                <p className="text-[10px] text-slate-400">Best for <strong>reading</strong> data from a collection.</p>
             </div>

             {/* Lower Bounded */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Lower Bounded</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Syntax: <code>? super T</code>. Accepts T or any superclass of T.
                </p>
                <p className="text-[10px] text-slate-400">Best for <strong>writing</strong> data into a collection.</p>
             </div>

             {/* Unbounded */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-yellow-600 mb-3 text-xs uppercase italic">3. Unbounded</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Syntax: <code>?</code>. Represents any type.
                </p>
                <p className="text-[10px] text-slate-400">Used when you only care about <strong>Object</strong> methods.</p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">WildcardDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.*;

public class Main {
    // Upper Bounded: Can read any list of numbers (Integer, Double, etc.)
    public static double sum(List<? extends Number> list) {
        double s = 0.0;
        for (Number n : list) s += n.doubleValue();
        return s;
    }

    public static void main(String[] args) {
        List<Integer> li = Arrays.asList(1, 2, 3);
        List<Double> ld = Arrays.asList(1.1, 2.2);

        System.out.println("Sum Integers: " + sum(li));
        System.out.println("Sum Doubles: " + sum(ld));
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>PECS Principle:</strong> Remember "Producer Extends, Consumer Super." If you are taking items out of a list (Producer), use <code>? extends</code>. If you are putting items into a list (Consumer), use <code>? super</code>. In your <strong>HRMS</strong>, use <code>? extends Employee</code> to process a list of <code>Managers</code> or <code>Developers</code> generically.
             </p>
          </div>
        </div>
      );
      case 'Bounded Types':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Bounded Type Parameters
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Bound types limit the range of types that can be passed to a generic. By using the <code>extends</code> keyword, you can call methods of the parent class inside your generic code safely.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Upper Bound */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Upper Bound (extends)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Restricts the type to a specific class or its subclasses. 
                   Syntax: <code>&lt;T extends Number&gt;</code>.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• Allows calling methods of the bound (e.g., <code>doubleValue()</code>).</li>
                   <li>• Works for both classes and interfaces.</li>
                </ul>
             </div>

             {/* Multiple Bounds */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm border-l-4 border-l-orange-500">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Multiple Bounds</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   A type can be bound to multiple interfaces.
                   Syntax: <code>&lt;T extends Class & Interface&gt;</code>.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• The class must come first in the list.</li>
                   <li>• Use the <code>&</code> symbol to separate bounds.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">SalaryCalculator.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// Restricting T to only Number or its subclasses (Integer, Double, etc.)
public class Calculator<T extends Number> {
    private T value;

    public Calculator(T value) { this.value = value; }

    public double square() {
        // Safe to call doubleValue() because T is guaranteed to be a Number
        return value.doubleValue() * value.doubleValue();
    }

    public static void main(String[] args) {
        Calculator<Integer> intCalc = new Calculator<>(10);
        System.out.println(intCalc.square());

        // Calculator<String> s; // Compile-time ERROR: String is not a Number
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>HRMS Use Case:</strong> In your **HRMS application**, if you're writing a generic <code>PayrollProcessor&lt;T&gt;</code>, you can use <code>&lt;T extends Employee & Payable&gt;</code>. This ensures the method only accepts objects that are both <code>Employee</code> entities and implement the <code>Payable</code> interface.
             </p>
          </div>
        </div>
      );
      case 'Generic Methods':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Generic Methods in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A generic method allows the type parameter to be scoped specifically to the method. This means you can have a non-generic class that contains highly flexible, type-safe generic methods.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Rules */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Rules of Syntax</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Type Parameter:</strong> Must appear before the return type (e.g., <code>&lt;T&gt; void</code>).</li>
                   <li>• <strong>Inference:</strong> Java usually "infers" the type based on the arguments you pass, so you don't always have to specify it.</li>
                   <li>• <strong>Scope:</strong> The type <code>T</code> is only valid within that specific method.</li>
                </ul>
             </div>

             {/* Static & Generics */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm border-l-4 border-l-orange-500">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Static Generic Methods</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Static methods <strong>cannot</strong> use the type parameters of their class. They must declare their own generic type parameters.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">Utility.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`public class DataUtils {
    // Generic Method to print any array
    public static <E> void printArray(E[] elements) {
        for (E element : elements) {
            System.out.println(element);
        }
    }

    public static void main(String[] args) {
        Integer[] intArray = { 10, 20, 30 };
        String[] strArray = { "Java", "React", "Native" };

        // Calling the same method with different types
        printArray(intArray); 
        printArray(strArray); 
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Tip:</strong> In your <strong>organic products e-commerce</strong> project, you could use a generic method to "shuffle" or "filter" lists of different objects like <code>Products</code>, <code>Categories</code>, or <code>Reviews</code> without writing separate logic for each.
             </p>
          </div>
        </div>
      );
      case 'Generic Classes':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Generic Classes in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Generics ensure <strong>Type Safety</strong> by catching type errors at compile-time rather than runtime. They allow you to write a single class that handles different types of data without using <em>Object</em> casting.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Why Generics? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">The Benefits</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Type Safety:</strong> Prevents <code>ClassCastException</code> by checking types during compilation.</li>
                   <li>• <strong>No Casting:</strong> You don't need to manually cast objects (e.g., <code>(String) obj</code>).</li>
                   <li>• <strong>Code Reusability:</strong> One class works for any Object type.</li>
                </ul>
             </div>

             {/* Type Parameters */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm border-l-4 border-l-orange-500">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Common Type Parameters</h4>
                <p className="text-[11px] text-slate-500 mb-2">By convention, we use single uppercase letters:</p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• <code>T</code>: Type</li>
                   <li>• <code>E</code>: Element (used in Collections)</li>
                   <li>• <code>K</code>: Key / <code>V</code>: Value (used in Maps)</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">Box.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// Defining a Generic Class
class Box<T> {
    private T data;
    
    public void set(T data) { this.data = data; }
    public T get() { return data; }
}

public class Main {
    public static void main(String[] args) {
        // Box for Strings
        Box<String> nameBox = new Box<>();
        nameBox.set("Ajay");
        
        // Box for Integers
        Box<Integer> idBox = new Box<>();
        idBox.set(101);

        System.out.println("Name: " + nameBox.get());
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Perspective:</strong> In your <strong>organic products project</strong>, you can create a generic <code>ApiResponse&lt;T&gt;</code> class. Whether you are fetching a list of <code>Products</code> or a single <code>User</code> profile, the same response wrapper can handle the data safely without losing its type identity.
             </p>
          </div>
        </div>
      );
      case 'Comparable vs Comparator':
      return (
        <div className= "space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Comparable vs. Comparator
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Use <strong>Comparable</strong> for a single, default sorting order. Use <strong>Comparator</strong> for multiple, custom sorting orders without modifying the original class.
            </p>
          </section>

          [Image of a diagram comparing Java Comparable and Comparator interfaces showing internal versus external sorting logic]

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Comparable */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Comparable (Internal)</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Interface:</strong> <code>java.lang.Comparable</code></li>
                   <li>• <strong>Method:</strong> <code>compareTo(Object obj)</code></li>
                   <li>• <strong>Logic:</strong> Defined inside the actual class being sorted.</li>
                   <li>• <strong>Usage:</strong> Provides a "Natural Sorting Order" (e.g., ID or Name).</li>
                </ul>
             </div>

             {/* Comparator */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Comparator (External)</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Interface:</strong> <code>java.util.Comparator</code></li>
                   <li>• <strong>Method:</strong> <code>compare(Object obj1, Object obj2)</code></li>
                   <li>• <strong>Logic:</strong> Defined in a separate class or as a Lambda.</li>
                   <li>• <strong>Usage:</strong> Provides multiple "Custom Sorting Orders" (e.g., Price, Rating).</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">SortingDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`// 1. Comparable: Default sorting by ID
class Product implements Comparable<Product> {
    int id;
    String name;
    public int compareTo(Product p) { return this.id - p.id; }
}

// 2. Comparator: Custom sorting by Name
class NameComparator implements Comparator<Product> {
    public int compare(Product p1, Product p2) {
        return p1.name.compareTo(p2.name);
    }
}

// Usage in Collections.sort(list, new NameComparator());`}
             </pre>
          </div>

          [Image of a table comparing Java Comparable and Comparator across categories like package, method name, and impact on the original class]

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Application:</strong> In your <strong>organic products e-commerce</strong> project, use <code>Comparable</code> to sort products by <code>ID</code> by default. Then, use <code>Comparator</code> to allow users to toggle sorting between "Price: Low to High" and "Customer Rating".
             </p>
          </div>
        </div>
      );
      case 'Iterator':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Java Iterator Interface
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              An <strong>Iterator</strong> is an object that can be used to loop through collections. It is the modern replacement for <em>Enumeration</em> in the Java Collections Framework.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Core Methods</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <code>hasNext()</code>: Returns true if the iteration has more elements.</li>
                   <li>• <code>next()</code>: Returns the next element in the iteration.</li>
                   <li>• <code>remove()</code>: Removes the last element returned by the iterator from the collection.</li>
                </ul>
             </div>

             {/* Why use Iterator? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">The Advantage</h4>
                <p className="text-[11px] text-slate-500 mb-2">
                   Standard loops fail if you try to modify a list while reading it. Iterator is the <strong>only safe way</strong> to remove elements during traversal.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">IteratorDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.*;

ArrayList<String> products = new ArrayList<>();
products.add("Organic Apple");
products.add("Non-Organic Seed");
products.add("Organic Banana");

// Getting the Iterator
Iterator<String> it = products.iterator();

while(it.hasNext()) {
    String item = it.next();
    if(item.contains("Non-Organic")) {
        it.remove(); // Safely removes the item from the list
    }
}
System.out.println(products); // Output: [Organic Apple, Organic Banana]`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Project Context:</strong> In your <strong>organic products e-commerce</strong> project, use an Iterator to filter out items from a <code>CartList</code> if their <code>stockQuantity</code> is zero before proceeding to checkout. This ensures your list stays synced without crashing your application.
             </p>
          </div>
        </div>
      );
      case 'Queue, PriorityQueue':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Queue & PriorityQueue
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Queues are essential for task scheduling and message handling. While a standard Queue processes items in the order they arrive, a <strong>PriorityQueue</strong> ensures the most important items are handled first.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The Queue Interface */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. Queue (FIFO)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Elements are added at the end and removed from the beginning. Commonly implemented by <code>LinkedList</code> or <code>ArrayDeque</code>.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• <code>add() / offer()</code>: Inserts an element.</li>
                   <li>• <code>remove() / poll()</code>: Returns and removes the head.</li>
                   <li>• <code>element() / peek()</code>: Returns the head without removing it.</li>
                </ul>
             </div>

             {/* PriorityQueue */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. PriorityQueue</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Elements are ordered according to their **natural ordering** or by a custom <code>Comparator</code>.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• Not FIFO: The head is the **least** element based on sorting.</li>
                   <li>• No Nulls: Does not allow null elements.</li>
                   <li>• Complexity: O(log n) for insertion and removal.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">QueueDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.*;

// Standard Queue using LinkedList
Queue<String> queue = new LinkedList<>();
queue.add("User 1");
queue.add("User 2");
System.out.println(queue.poll()); // "User 1" (First In, First Out)

// PriorityQueue: Lowest number has highest priority
PriorityQueue<Integer> pq = new PriorityQueue<>();
pq.add(50);
pq.add(10);
pq.add(30);
System.out.println(pq.poll()); // 10 (Smallest value comes out first)`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Application:</strong> In your **HRMS project**, use a standard <code>Queue</code> to manage a line of employees waiting for biometric registration. In your **API Admin Portal**, use a <code>PriorityQueue</code> to process incoming API requests where "Admin" requests have a higher priority value than "Guest" requests.
             </p>
          </div>
        </div>
      );
      case 'TreeSet, TreeMap':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              TreeSet & TreeMap (Sorted Collections)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Use these when you need elements to stay in their "natural" order (like alphabetical or numerical) or a custom order defined by a <code>Comparator</code>.
            </p>
          </section>

          [Image of a Red-Black Tree data structure diagram showing nodes with keys and child pointers used in Java TreeSet and TreeMap]

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* TreeSet */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">TreeSet (Unique & Sorted)</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Order:</strong> Maintains elements in ascending order.</li>
                   <li>• <strong>Uniqueness:</strong> Does not allow duplicate elements.</li>
                   <li>• <strong>Nulls:</strong> Does <strong>not</strong> allow null elements (throws NullPointerException).</li>
                   <li>• <strong>Performance:</strong> O(log n) for basic operations like add and remove.</li>
                </ul>
             </div>

             {/* TreeMap */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">TreeMap (Sorted Keys)</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Order:</strong> Sorts the map based on the <strong>natural order of its keys</strong>.</li>
                   <li>• <strong>Structure:</strong> Stores data as Key-Value pairs.</li>
                   <li>• <strong>Keys:</strong> Cannot have a null key, but can have multiple null values.</li>
                   <li>• <strong>Navigation:</strong> Provides methods like <code>firstKey()</code> and <code>lastKey()</code>.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">SortedDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.*;

// TreeSet: Automatically Alphabetical
TreeSet<String> set = new TreeSet<>();
set.add("Zebra");
set.add("Apple");
System.out.println(set); // Output: [Apple, Zebra]

// TreeMap: Keys are sorted numerically
TreeMap<Integer, String> map = new TreeMap<>();
map.put(50, "Half");
map.put(10, "Ten");
System.out.println(map); // Output: {10=Ten, 50=Half}`}
             </pre>
          </div>

          [Image of a table comparing HashSet, LinkedHashSet, and TreeSet across categories like ordering and performance]

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Project Usage:</strong> In your <strong>LMS project</strong>, use a <code>TreeSet</code> to store a list of student names so they are always displayed alphabetically. In the <strong>HRMS</strong>, use a <code>TreeMap</code> with <code>SalaryAmount</code> as the key to easily find the "highest" or "lowest" earners using <code>lastKey()</code> or <code>firstKey()</code>.
             </p>
          </div>
        </div>
      );
      case 'HashSet, LinkedHashSet':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              HashMap & LinkedHashMap
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Both classes allow you to store unique keys mapped to specific values. While a <strong>HashMap</strong> is optimized for speed, a <strong>LinkedHashMap</strong> is designed for predictability in order.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* HashMap */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">HashMap (Unordered)</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Order:</strong> No guarantee of any specific order; it can change over time.</li>
                   <li>• <strong>Performance:</strong> Best performance for <code>get()</code> and <code>put()</code> operations (O(1)).</li>
                   <li>• <strong>Nulls:</strong> Allows one null key and multiple null values.</li>
                </ul>
             </div>

             {/* LinkedHashMap */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm border-l-4 border-l-orange-500">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">LinkedHashMap (Ordered)</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Order:</strong> Maintains <strong>Insertion Order</strong> (the order keys were added).</li>
                   <li>• <strong>Memory:</strong> Consumes more memory than HashMap to maintain the doubly linked list.</li>
                   <li>• <strong>Special Feature:</strong> Can also be configured to track <strong>Access Order</strong> (useful for LRU caches).</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">MapDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.*;

// HashMap Example
Map<String, String> hMap = new HashMap<>();
hMap.put("IN", "India");
hMap.put("US", "USA");
System.out.println(hMap); // Output: {US=USA, IN=India} (Random order)

// LinkedHashMap Example
Map<String, String> lMap = new LinkedHashMap<>();
lMap.put("IN", "India");
lMap.put("US", "USA");
System.out.println(lMap); // Output: {IN=India, US=USA} (Insertion order)`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Perspective:</strong> In your <strong>HRMS project</strong>, use a <code>HashMap</code> to store <code>EmployeeID</code> vs <code>EmployeeObject</code> for the fastest possible lookup. However, if you are building the <strong>API Admin Portal</strong> and want to show the last 10 configuration changes in the order they were made, a <code>LinkedHashMap</code> is the better choice.
             </p>
          </div>
        </div>
      );
      case 'HashMap, LinkedHashMap':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              HashSet & LinkedHashSet
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Both classes guarantee that no duplicate elements exist in the collection. The primary choice between them depends on whether the <strong>order</strong> of insertion matters for your logic.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* HashSet */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">HashSet (Unordered)</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Mechanism:</strong> Uses a Hash Table (HashMap) internally.</li>
                   <li>• <strong>Order:</strong> Does <strong>not</strong> guarantee any specific order.</li>
                   <li>• <strong>Performance:</strong> Offers the best performance (O(1) for basic operations).</li>
                   <li>• <strong>Nulls:</strong> Allows one null element.</li>
                </ul>
             </div>

             {/* LinkedHashSet */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">LinkedHashSet (Ordered)</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Mechanism:</strong> Uses a Hash Table and a Doubly Linked List.</li>
                   <li>• <strong>Order:</strong> Maintains the <strong>insertion order</strong> of elements.</li>
                   <li>• <strong>Performance:</strong> Slightly slower than HashSet due to maintaining pointers.</li>
                   <li>• <strong>Nulls:</strong> Also allows one null element.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">SetDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.*;

Set<String> hashSet = new HashSet<>();
hashSet.add("Banana");
hashSet.add("Apple");
System.out.println(hashSet); // Output might be [Apple, Banana] (No Order)

Set<String> linkedSet = new LinkedHashSet<>();
linkedSet.add("Banana");
linkedSet.add("Apple");
System.out.println(linkedSet); // Output will be [Banana, Apple] (Insertion Order)`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Project Context:</strong> In your <strong>organic products project</strong>, use a <code>HashSet</code> to store a list of unique <code>ZipCodes</code> you deliver to, as order doesn't matter. However, use a <code>LinkedHashSet</code> for <code>RecentlyViewedCategories</code> if you want to show the user their history in the exact order they clicked them.
             </p>
          </div>
        </div>
      );
      case 'LinkedList':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              LinkedList in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A <strong>LinkedList</strong> implements both the <code>List</code> and <code>Deque</code> interfaces. It is best used when your application requires frequent insertions and deletions.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Key Features</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>No Shifting:</strong> Adding or removing elements doesn't require shifting other elements.</li>
                   <li>• <strong>Memory:</strong> Uses more memory than ArrayList because it stores two pointers per node.</li>
                   <li>• <strong>Access:</strong> Slower random access (O(n)) compared to ArrayList.</li>
                   <li>• <strong>Interface:</strong> Can act as a List, Stack, or Queue.</li>
                </ul>
             </div>

             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Essential Methods</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <code>addFirst() / addLast()</code>: Inserts at the start or end.</li>
                   <li>• <code>removeFirst() / removeLast()</code>: Efficiently deletes from ends.</li>
                   <li>• <code>peek()</code>: Looks at the first element without removing it.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <pre className="text-red-200 text-xs">
{`import java.util.LinkedList;

LinkedList<String> tasks = new LinkedList<>();
tasks.add("Fetch Data");
tasks.addFirst("Initialize API"); // Becomes first
tasks.addLast("Cleanup");       // Becomes last

System.out.println(tasks.getFirst()); // "Initialize API"`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>HRMS Context:</strong> In your **HRMS application**, you might use a <code>LinkedList</code> to manage a undo/redo history for employee record edits. Since you are mostly adding and removing from the ends of the list, it's more efficient than an ArrayList.
             </p>
          </div>
        </div>
      );
      case 'ArrayList':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              ArrayList in Java
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Unlike standard arrays, an <strong>ArrayList</strong> grows and shrinks dynamically as you add or remove elements. It is part of the <code>java.util</code> package.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Key Features</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Dynamic Resizing:</strong> Automatically handles capacity.</li>
                   <li>• <strong>Ordered:</strong> Maintains the insertion order.</li>
                   <li>• <strong>Index-based:</strong> Fast random access (O(1) time complexity).</li>
                   <li>• <strong>Duplicates:</strong> Allows multiple nulls and duplicate values.</li>
                </ul>
             </div>

             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Common Methods</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <code>add(element)</code>: Appends to the end.</li>
                   <li>• <code>get(index)</code>: Retrieves element at position.</li>
                   <li>• <code>remove(index)</code>: Deletes element and shifts others.</li>
                   <li>• <code>size()</code>: Returns the number of elements.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <pre className="text-red-200 text-xs">
{`import java.util.ArrayList;

ArrayList<String> employees = new ArrayList<>();
employees.add("Ajay");    // [Ajay]
employees.add("Mahanth"); // [Ajay, Mahanth]
employees.get(0);         // Returns "Ajay"
employees.size();         // Returns 2`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>HRMS Tip:</strong> Use an <code>ArrayList</code> to store the list of employees currently clocked in. Since you'll frequently display this list in your UI, the fast index-based access is ideal.
             </p>
          </div>
        </div>
      );
      case 'Collections':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              List, Set, and Map
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The Java Collections Framework standardizes the way groups of objects are handled. Understanding these three interfaces is critical for efficient data management in any application.
            </p>
          </section>

          

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {/* List Interface */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">1. List (Ordered)</h4>
                <p className="text-[11px] text-slate-500 mb-3">
                   An ordered collection that allows <strong>duplicate</strong> elements. Elements are accessed by their index.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400 italic">
                   <li>• ArrayList (Fast access)</li>
                   <li>• LinkedList (Fast insertion)</li>
                </ul>
             </div>

             {/* Set Interface */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">2. Set (Unique)</h4>
                <p className="text-[11px] text-slate-500 mb-3">
                   A collection that contains <strong>no duplicate</strong> elements. It models the mathematical set abstraction.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400 italic">
                   <li>• HashSet (Unordered)</li>
                   <li>• TreeSet (Sorted)</li>
                </ul>
             </div>

             {/* Map Interface */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-yellow-600 mb-3 text-xs uppercase italic">3. Map (Key-Value)</h4>
                <p className="text-[11px] text-slate-500 mb-3">
                   An object that maps <strong>keys to values</strong>. Keys must be unique, but values can be duplicated.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400 italic">
                   <li>• HashMap (General use)</li>
                   <li>• TreeMap (Sorted keys)</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">CollectionsDemo.java</span>
             </div>
             <pre className="text-red-200 text-xs">
{`import java.util.*;

public class Main {
    public static void main(String[] args) {
        // List: Maintains order, allows duplicates
        List<String> list = new ArrayList<>();
        list.add("Apple");
        list.add("Apple"); // Allowed

        // Set: Only unique elements
        Set<String> set = new HashSet<>();
        set.add("Apple");
        set.add("Apple"); // Ignored

        // Map: Key-Value pairs
        Map<Integer, String> map = new HashMap<>();
        map.put(101, "Ajay");
        map.put(102, "Mahanth");
    }
}`}
             </pre>
          </div>

          

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Dev Usage:</strong> In your <strong>organic e-commerce project</strong>, use a <code>List</code> for the shopping cart items, a <code>Set</code> for unique product categories, and a <code>Map</code> to store <code>ProductID</code> as the key with the <code>ProductObject</code> as the value for quick lookups.
             </p>
          </div>
        </div>
      );


    
    default:
      return (
        <div className="flex items-center justify-center h-64 text-slate-400 italic">
          Select a Java topic to start learning.
        </div>
      );
  }
};